<template>
  <div>
    <!-- Overlay Background -->
    <v-overlay
      v-model="showSuccess"
      :scrim="true"
      opacity="0.3"
      persistent
      :z-index="9998"
    >
    </v-overlay>
    
    <!-- Success Toast -->
    <transition name="fade-slide">
      <output v-if="showSuccess" class="success-toast">
        <img src="@/assets/mdi_success-bold.svg" alt="img" width="20" height="20" />
        <span style="font-size: 14px; font-weight: 600;">{{ SuccessMessage }}</span>
      </output>
    </transition>

    <v-container fluid class="px-0">
      <!-- Main Content Card -->
      <div class="main-card">
        <!-- Modern Tabs -->
        <div class="tabs-container">
          <div class="tabs-scroll">
            <div class="tabs-wrapper">
              <div
                v-for="(item, index) in items"
                :key="item.tab"
                class="tab-item d-flex flex-column align-center justify-between"
                @click="tab = index"
              >
                <div class="tab-icon-container mb-1"
                  :class="{active_tab : index === tab}">
                  <img :src="tab === index ? item.icon.white : item.icon.black" alt="icon" class="tab-icon" width="22" height="22" />
                </div>
                <span class="tab-text text-center" :class="{active_text : index === tab}">{{ $t(item.tab) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">
          <div v-for="(item, index) in items" :key="item.tab">
            <div v-if="index === tab" class="tab-panel">
              <!-- Token Management Panel -->
              <div
                v-if="item.dataResponse != null"
                class="token-management-panel"
              >
                <div class="token-content">
                  <!-- Action Bar -->
                  <div class="action-bar">
                    <div class="">
                      <h2 class="font-weight-bold">{{ $t('Token List') }}</h2>
                    </div>
                    <div class="d-flex" style="gap: 24px;">
                      <v-text-field
                        id="token-name-field-search-userchatmanagement"
                        class="tokenNameInput"
                        :placeholder="$t('Token Name')"
                        density="comfortable"
                        variant="outlined"
                        hide-details
                        color="primary"
                        v-model="searchTokenName"
                        @keydown.enter="SearchTokenName"
                      >
                        <template #prepend-inner>
                          <img 
                            src="@/assets/Icons/new_search_outline.svg" 
                            alt="search" 
                            style="width:20px;height:20px;margin-right:8px;"
                          />
                        </template>
                      </v-text-field>
                      <button class="add-button" @click="Add()">
                        <v-icon size="22">mdi-plus</v-icon>
                        <span>{{ $t("add") }}</span>
                      </button>
                    </div>
                  </div>

                  <!-- Modern Data Table -->
                  <div class="table-container">
                    <v-data-table-server
                      v-model:options="options"
                      :headers="headers2"
                      :items="item.dataResponse"
                      item-key="TokenName"
                      :items-per-page="options.itemsPerPage"
                      :items-per-page-options="[5, 10, 25, 50, 100]"
                      :items-length="searchTotalTable"
                      :loading="dialog"
                      class="modern-table"
                      :loading-text="$t('loadingpleasewait')"
                    >
                      <template #no-data>
                        <div style="text-align: center;">
                          <span style="color: #646464; font-weight: 400;">{{ $t('noDataAvailable') }}</span>
                        </div>
                      </template>
                      <!-- API Key Display -->
                      <template v-slot:item.apI_Key="{ item }">
                        <div class="api-key-container">
                          <div class="api-key-display">
                            <input
                              :id="'apikey-input-' + item.TokenName"
                              aria-label="API Key"
                              :type="item.show_status ? 'text' : 'password'"
                              :value="item.apI_Key"
                              class="api-key-input"
                              readonly
                            />
                            <button class="icon-button ml-1" @click="ShowAPIKey(item)">
                              <v-icon size="20" :color="item.show_status ? '#2ABF56' : ''">{{
                                item.show_status ? "mdi-eye" : "mdi-eye-off"
                              }}</v-icon>
                              <v-tooltip v-if="item.show_status" activator="parent" location="top">
                                {{ $t('Visible') }}
                              </v-tooltip>
                              <v-tooltip v-else activator="parent" location="top">
                                {{ $t('Hidden') }}
                              </v-tooltip>
                            </button>
                            <button
                              v-if="item.show_status"
                              class="icon-button copy-button"
                              @click="ClipboardAPIKey(item)"
                            >
                              <img src="@/assets/Icons/new_copy.png" width="30" height="30" alt="">
                              <v-tooltip activator="parent" location="top">
                                {{ $t('copy') }}
                              </v-tooltip>
                            </button>
                          </div>
                          <div v-if="item.copy_status" class="copy-toast">
                            Copied
                          </div>
                        </div>
                      </template>

                      <!-- Status Badge -->
                      <template v-slot:item.status="{ item }">
                        <div
                          class="status-badge"
                          :class="{ active: item.status, expired: !item.status }"
                        >
                          {{ item.status ? $t('Active') : $t('Expired') }}
                        </div>
                      </template>

                      <!-- Actions Menu -->
                      <template v-slot:item.actions="{ item }">
                        <div class="d-flex justify-center items-center" style="gap: 16px;">
                          <button class="d-flex justify-center items-center" @click="displayActions(item, 0)">
                            <img src="@/assets/Icons/new_renew.png" alt="renew" width="24" height="24" />
                            <v-tooltip activator="parent" location="top">
                              {{ $t('renew') }}
                            </v-tooltip>
                          </button>
                          <button class="d-flex justify-center items-center" @click="displayActions(item, 1)">
                            <img src="@/assets/Icons/new_revoke.png" alt="revoke" width="24" height="24" />
                            <v-tooltip activator="parent" location="top">
                              {{ $t('revoke') }}
                            </v-tooltip>
                          </button>
                        </div>
                      </template>

                      <template v-slot:bottom>
                        <div
                          class="d-flex justify-space-between align-center px-4"
                          style="width: 100%; margin-top: 24px;"
                        >
                          <div>{{ $t("totalResults") }} {{ searchTotalTable }}</div>

                          <v-data-table-footer
                            :items="[
                              { text: '10', value: 10 },
                              { text: '25', value: 25 },
                              { text: '50', value: 50 },
                              { text: '100', value: 100 },
                              { text: 'All', value: -1 },
                            ]"
                            class="pa-0"
                            :page="options.page"
                            :length="Math.ceil(searchTotalTable / itemsPerPage)"
                          />
                        </div>
                      </template>
                    </v-data-table-server>
                  </div>
                </div>
              </div>

                <!-- API Documentation Panel -->
                <div v-else class="api-docs-panel">
                  <div class="docs-layout">
                    <!-- Left Side - Parameters -->
                    <div class="docs-left" :class="{no_container: index != 1}">
                      <div
                        v-for="(child, no_run) in item.scriptAndParam"
                        :key="child.content"
                        class="param-item"
                        :class="{param_div: index != 1 && no_run == 0, importUser_div: index == 8 && no_run == 0}"
                      >
                        <SectionHeading
                          v-if="no_run == 0"
                          :icon="sectionIcons.parameter"
                          :label="$t('Parameters')"
                        />

                        <div 
                          :class="{
                            request_div_top: no_run === 1 && index > 1, 
                            request_div_middle: no_run > 1 && no_run < item.scriptAndParam.length && index > 1,
                            request_div_bottom: no_run === item.scriptAndParam.length - 1 && index > 1,
                            request_div_top_importUser: no_run === 1 && index === 8,
                          }"
                        >
                          <SectionHeading
                            v-if="no_run === 1 && index > 1 && index !== 8"
                            :icon="sectionIcons.request"
                            :label="$t('Request')"
                          />

                          <div class="param-header">
                            <h4 class="param-name">{{ child.content }}</h4>
                            <span v-if="child.dataType" class="param-type">{{
                              child.dataType
                            }}</span>
                            <v-tooltip v-if="child.tooltip" location="top start" max-width="380px">
                              <template v-slot:activator="{ props }">
                                <img src="@/assets/Icons/new_info_icon.png" alt="Parameter info" width="18" height="18" v-bind="props" />
                              </template>
                              <span>{{ child.tooltip }}</span>
                            </v-tooltip>
                          </div>

                          <div class="param-input">
                            <textarea
                              :id="'param-script-' + index + '-' + no_run"
                              :aria-label="child.content"
                              v-model="child.script"
                              :readonly="child.readonly"
                              class="modern-textarea"
                              @input="updateExampleCode(index)"
                              @focus="$event.target.select()"
                            ></textarea>
                            <button
                              class="copy-param-button"
                              @click="copyClipboard(no_run, 'left')"
                            >
                              <img src="@/assets/Icons/new_copy.png" width="26" height="26" alt="Copy">
                              <v-tooltip activator="parent" location="top">
                                {{ $t('copy') }}
                              </v-tooltip>
                            </button>
                          </div>
                        </div>
                      </div>

                      <!-- Response Section -->
                      <div
                        v-if="item.responseStatus || item.responseDescription"
                        class="response-section"
                      >
                        <SectionHeading
                          :icon="sectionIcons.response"
                          :label="$t('Response')"
                          wrapper-class="docs-title mb-3"
                        />

                        <div
                          v-if="item.responseStatus"
                          class="status-responses"
                        >
                          <div
                            v-for="status in item.responseStatus"
                            :key="status.responseMsg"
                            class="status-item"
                          >
                            <div class="status-code">
                              {{ status.responseStatus }}
                            </div>
                            <div class="status-message">
                              {{ status.responseMsg }}
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="item.responseDescription"
                          class="response-table"
                        >
                          <v-data-table
                            :headers="headers"
                            :items="item.responseDescription"
                            item-key="key"
                            hide-default-footer
                            disable-sort
                            class="modern-response-table"
                          />
                        </div>
                      </div>
                    </div>

                    <!-- Right Side - Code Examples -->
                    <div class="docs-right">
                        <div class="code-header">
                          <v-row>
                            <v-col cols="6">
                              <h3 class="section-title mb-0 section-heading-h">{{ $t('Code Examples') }}</h3>
                            </v-col>
                            <v-col cols="6">
                              <v-select
                                v-if="tab > 2"
                                v-model="selectVersion"
                                :items="versions"
                                item-title="title"
                                item-value="value"
                                class="modern-input d-flex align-center justify-end"
                                density="comfortable"
                                variant="outlined"
                                hide-details
                                @update:modelValue="switchVersion"
                              />
                            </v-col>
                          </v-row>
                        </div>

                        <div
                          v-for="(child, no_run) in item.example"
                          :key="child.contentEx"
                          class="code-example"
                        >
                          <div class="code-label">{{ child.contentEx }}</div>
                          <div class="code-container">
                            <prism-editor
                              id="codeEditor"
                              class="my-editor"
                              v-model="child.codeEx"
                              :highlight="highlighter"
                              :line-numbers="true"
                              :auto-style-line-numbers="true"
                              :ignore-tab-key="true"
                              readonly
                            />
                            <button
                              class="copy-code-button"
                              @click="copyClipboard(no_run, 'right')"
                            >
                              <img src="@/assets/Icons/new_copy_white.png" width="26" height="26" alt="Copy">
                              <v-tooltip activator="parent" location="top">
                                {{ $t('copy') }}
                              </v-tooltip>
                            </button>
                          </div>
                        </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </div>

      <!-- Dialogs remain the same but with updated styling -->
      <!-- Show Token Dialog -->
      <v-dialog
        v-model="dialogShowToken"
        max-width="600px"
        class="modern-dialog"
      >
        <v-card class="dialog-card">
          <div class="dialog-header">
            <div class="header-icon">
              <v-icon size="24" color="primary">mdi-key-variant</v-icon>
            </div>
            <div class="header-content">
              <h3 class="dialog-title">{{ $t('_API_Key') }}</h3>
              <p class="dialog-subtitle">{{ $t('_YourSecureAccessToken') }}</p>
            </div>
            <v-btn
              icon
              variant="text"
              size="small"
              @click="dialogShowToken = false"
              class="close-btn"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <div class="dialog-body">
            <div class="usage-info">
              <div class="info-card">
                <v-icon size="16" color="info" class="info-icon"
                  >mdi-information</v-icon
                >
                <span
                  >Use this key in your application by passing it with the
                  <code class="inline-code">key=API_KEY</code> parameter.</span
                >
              </div>
            </div>

            <div class="token-display-container">
              <span class="token-label">{{ $t('Your API Key') }}</span>
              <div class="token-input-wrapper">
                <input
                  id="apikey-token-input-settingexternalconnector"
                  aria-label="Your API Key"
                  :value="token"
                  readonly
                  class="token-input"
                  @focus="$event.target.select()"
                />
                <v-tooltip :text="$t('Copy to clipboard')" location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      id="icon-copy-settingexternalconnector"
                      v-bind="props"
                      icon
                      variant="text"
                      size="small"
                      class="copy-btn"
                      @click="ClipboardAPIKey(editedIndex)"
                    >
                      <v-icon size="18">mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>
            </div>
          </div>

          <div class="dialog-footer">
            <v-btn
              variant="elevated"
              color="primary"
              @click="dialogShowToken = false"
              class="primary-btn"
            >
              {{ $t('gotIt') }}
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- Add/Renew Dialog -->
      <v-dialog
        v-model="dialogRenew"
        max-width="700px"
        persistent
        class="modern-dialog"
      >
        <v-card class="dialog-card">
          <!-- Updated Dialog Header -->
          <div class="dialog-header"
          :class="{ 'dialog-header-add': flagAdd, 'dialog-header-edit': !flagAdd }"
          >
            <div class="header-main">
              <div class="header-text-content px-2">
                <h3 class="dialog-title mb-1" style="font-size: 22px">
                  {{ flagAdd ? $t('Create New Token') : $t('Renew Token') }}
                </h3>
                <p class="dialog-subtitle mb-0" style="font-size: 14px; max-width: 100%">
                  {{
                    flagAdd
                      ? $t('_CreateTokenSubtitle')
                      : $t('_RenewTokenSubtitle')
                  }}
                </p>
              </div>
            </div>
          </div>

          <div class="dialog-body py-5">
            <!-- Basic Information -->
            <div class="form-section">
              <SectionHeading
                :icon="sectionIcons.basicInfo"
                :label="$t('basicInfo')"
                wrapper-class="section-title"
              />
              <div class="form-grid-2">
                <div class="form-group">
                  <span class="form-label">{{ $t('Token Name') }}<span style="color: #CC1F47" class="ml-1">*</span></span>
                  <v-text-field
                    v-model="tokenName"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :disabled="!flagAdd"
                    :placeholder="$t('Enter Token Name')"
                    class="modern-input"
                  />
                </div>
                <div class="form-group">
                  <span class="form-label">{{ $t('Email Address') }}<span style="color: #CC1F47" class="ml-1">*</span></span>
                  <v-text-field
                    v-model="email"
                    :rules="[rules.required, validateEmail(email)]"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :placeholder="$t('Enter Email Address')"
                    class="modern-input"
                  />
                </div>
              </div>

              <!-- Current Token Display (Renew only) -->
              <div v-if="!flagAdd" class="current-token mb-3">
                <span class="form-label">{{ $t('Current API Key') }}</span>
                <div class="token-display">
                  <input
                    id="current-apikey-token-input-settingexternalconnector"
                    aria-label="Current API Key"
                    :value="token"
                    readonly
                    class="current-token-input"
                    @focus="$event.target.select()"
                  />
                  <v-tooltip :text="$t('copy')" location="top">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon
                        variant="text"
                        size="small"
                        class=""
                        @click="ClipboardAPIKey(editedItem)"
                      >
                        <img src="@/assets/Icons/new_copy.png" width="26" height="26" alt="Copy">
                      </v-btn>
                    </template>
                  </v-tooltip>
                </div>
              </div>
            </div>

            <!-- Expiration Settings -->
            <div class="form-section">
              <SectionHeading
                :icon="sectionIcons.expiration"
                :label="$t('Expiration Settings')"
                wrapper-class="section-title"
              />
              <v-radio-group v-model="radios" class="radio-group-modern">
                <div class="radio-option">
                  <v-radio value="DDLSelectDate" class="custom-radio">
                    <template v-slot:label>
                      <div class="radio-content">
                        <span class="radio-title">{{ $t('Preset Duration') }}</span>
                        <span class="radio-description"
                          >{{ $t('Choose from common durations') }}</span
                        >
                      </div>
                    </template>
                  </v-radio>
                  <div v-if="radios === 'DDLSelectDate'" class="radio-input">
                    <v-select
                      v-model="DateSelected"
                      :items="ListDate"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      density="comfortable"
                      hide-details
                      class="duration-select-modern"
                    />
                  </div>
                </div>

                <div class="radio-option">
                  <v-radio value="CustomeSelectDate" class="custom-radio">
                    <template v-slot:label>
                      <div class="radio-content">
                        <span class="radio-title">{{ $t('Custom Date') }}</span>
                        <span class="radio-description"
                          >{{ $t('Set a specific expiration date') }}</span
                        >
                      </div>
                    </template>
                  </v-radio>
                  <div
                    v-if="radios === 'CustomeSelectDate'"
                    class="radio-input"
                  >
                    <v-menu
                      v-model="menuCustomeDate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      location="bottom start"
                      max-width="290px"
                    >
                      <template v-slot:activator="{ props }">
                        <v-text-field
                          v-model="EndDate"
                          v-bind="props"
                          variant="outlined"
                          density="comfortable"
                          hide-details
                          readonly
                          :placeholder="$t('Select date')"
                          class="date-input-modern"
                        >
                          <template v-slot:prepend-inner>
                            <img src="@/assets/Icons/new_calendar.png" alt="calendar" style="width: 20px; height: 20px;" />
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="EndDate"
                        :year="endPickerYear"
                        :month="endPickerMonth"
                        @update:year="endPickerYear = $event"
                        @update:month="endPickerMonth = $event"
                        :min="nowDate"
                        :max="getEndDate"
                        elevation="8"
                        rounded="lg"
                        @update:model-value="
                          (val) => {
                            EndDate = formatDate(val);
                            menuCustomeDate = false;
                          }
                        "
                        show-adjacent-months
                        class="date-picker">
                        <template #header>
                          <div class="picker-header-custom d-flex align-center justify-space-between px-4 py-3">
                            <div class="text-h6 font-weight-medium">{{ getFormattedHeaderDate(endPickerYear, endPickerMonth, EndDate) }}</div>
                            <v-select
                              v-model="endPickerYear"
                              :items="years"
                              density="compact"
                              variant="plain"
                              hide-details
                              class="month-year-select"
                              style="width: 100px;"
                            ></v-select>
                          </div>
                        </template>
                      </v-date-picker>
                    </v-menu>
                  </div>
                </div>
              </v-radio-group>
            </div>

            <!-- Model Configuration -->
            <div class="form-section">
              <SectionHeading
                :icon="sectionIcons.setting"
                :label="$t('modelConfiguration')"
                wrapper-class="section-title"
              />
              <div class="form-grid-2 mb-3">
                <div class="form-group">
                  <span class="form-label">{{ $t('ModelDisplay') }}</span>
                  <v-autocomplete
                    v-model="GPTModel"
                    :items="DDGPTModel"
                    item-title="name"
                    item-value="modelDisplayID"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    :placeholder="$t('Select Model')"
                    class="modern-input"
                    clearable
                  />
                </div>
                <div class="form-group">
                  <span class="form-label">{{ $t('modelGroup') }}</span>
                  <v-autocomplete
                    v-model="GroupModel"
                    :items="DDGroupModel"
                    item-title="name"
                    item-value="id"
                    variant="outlined"
                    density="comfortable"
                    hide-details
                    clearable
                    :placeholder="$t('Select Model Group')"
                    class="modern-input"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="dialog-actions">
            <div class="actions-content">
              <div class="actions-left"></div>
              <div class="actions-right">
                <v-btn
                  variant="outlined"
                  @click="dialogRenew = false"
                  class="dialog-action-btn cancel-btn"
                >
                  {{ $t('cancel') }}
                </v-btn>
                <v-btn
                  variant="elevated"
                  :loading="dialog"
                  @click="flagAdd ? CreateNewAPIToken() : Renew()"
                 class="dialog-action-btn"
                 :class="flagAdd ? 'save-btn-add' : 'save-btn-edit'"
                 style="box-shadow: none; transform: none;"
                >
                  <img v-show="!flagAdd" src="@/assets/Icons/new_renew_white.svg" alt="renew" width="22" height="22" class="mr-1"/>
                  {{ flagAdd ? $t('Create Token') : $t('Renew Token') }}
                </v-btn>
              </div>
            </div>
          </div>
        </v-card>
      </v-dialog>

      <!-- Revoke Dialog -->
      <v-dialog
        v-model="dialogRevoke"
        max-width="600px"
        persistent
        class="modern-dialog"
      >
        <v-card class="dialog-card">
          <div class="dialog-header dialog-header-backlist">
            <div class="header-main">
              <div class="header-text-content">
                <h3 class="dialog-title mb-0" style="color: #C32E0E !important; font-size: 22px">{{ $t('Revoke Token') }}</h3>
              </div> 
            </div>
          </div>

          <div class="dialog-body">
            <div class="warning-container pa-0">
              <div class="warning-text">
                <p class="warning-message">
                  {{ $t('_RevokeTokenWarning') }}
                </p>
                <div class="token-info">
                  <span class="token-label-small">{{ $t('Token Name') }}:</span>
                  <span class="token-name-display">{{ tokenName }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="dialog-footer">
            <v-btn
              variant="text"
              @click="dialogRevoke = false"
              class="cancel-btn"
            >
              {{ $t('Keep Token') }}
            </v-btn>
            <v-btn
              variant="elevated"
              color="error"
              @click="Revork"
              class="danger-btn"
            >
              <img src="@/assets/Icons/new_revoke_white.png" alt="revoke" width="22" height="22" class="mr-1"/>
              {{ $t('revoke') }}
            </v-btn>
          </div>
        </v-card>
      </v-dialog>

      <!-- Loading Overlay -->
      <v-overlay :value="dialog" class="loading-overlay">
        <div class="loading-content">
          <v-progress-circular indeterminate size="64" color="primary" />
          <p class="loading-text">{{ $t('_Loading') }}</p>
        </div>
      </v-overlay>
    </v-container>

    <!-- warning dialog -->
    <v-dialog v-model="showWarningDialog" max-width="570">
      <v-card class="warning-card">
        <v-card-title class="card-dialog-title py-4 px-5" style="background: linear-gradient(100deg, #FFF8ED 0%, #FFF0DA 100%);">
          <span class="font-weight-bold" style="color: #FFA726 !important;">{{warningTitle}}</span>
          <v-icon
            v-show="showButton"
            size="30"
            class="cursor-pointer float-end"
            @click="closeWarningDialog"
            style="color: black;"
          >
            mdi-close
          </v-icon>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-6 pb-4 px-4 body-text" style="font-weight: 500">
          {{warningMessage}}
        </v-card-text>
        <hr style="border: none; border-top: 1px solid #c9c9c9;" />
        <v-card-actions v-show="showButton" class="px-6 pb-3 pt-3 pl-8 justify-end" style="gap: 12px;">
          <button type="button" class="warning-btn" @click="closeWarningDialog" :disabled="deleting">{{ $t('OK') }}</button>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import dateUtils from "../plugins/date-utils";
import moment from "moment";
import LogTrace from "@/api/function.js";
import { PrismEditor } from "vue-prism-editor";
import logoImg from "@/assets/images/chatframework 1.png";
import bgImg from "@/assets/images/bg-ai.png";
import "vue-prism-editor/dist/prismeditor.min.css"; // import the styles somewhere

// import highlighting library (you can use any library you want just return html string)
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/themes/prism-okaidia.css";
import "prismjs/components/prism-rust";

import "prismjs/plugins/line-numbers/prism-line-numbers.js";
import "prismjs/plugins/line-numbers/prism-line-numbers.css";
import tokenIcon from "@/assets/Icons/new_token_management.svg";
import tokenIconWhite from "@/assets/Icons/new_token_management_white.svg";
import snippetIcon from "@/assets/Icons/new_snippet.svg";
import snippetIconWhite from "@/assets/Icons/new_snippet_white.svg";
import loginExternalIcon from "@/assets/Icons/new_login_external.svg";
import loginExternalIconWhite from "@/assets/Icons/new_login_external_white.svg";
import vectorIcon from "@/assets/Icons/new_vector_insert.svg";
import vectorIconWhite from "@/assets/Icons/new_vector_insert_white.svg";
import listIcon from "@/assets/Icons/new_list_vector_insert.svg";
import listIconWhite from "@/assets/Icons/new_list_vector_insert_white.svg";
import selectorIcon from "@/assets/Icons/new_selector_model.svg";
import selectorIconWhite from "@/assets/Icons/new_selector_model_white.svg";
import chatPostIcon from "@/assets/Icons/new_chat_post.svg";
import chatPostIconWhite from "@/assets/Icons/new_chat_post_white.svg";
import chatPromptIcon from "@/assets/Icons/new_chat_promptPost.svg";
import chatPromptIconWhite from "@/assets/Icons/new_chat_promptPost_white.svg";
import importUserIcon from "@/assets/Icons/new_import_user.svg";
import importUserIconWhite from "@/assets/Icons/new_import_user_white.svg";
import importUserChatIcon from "@/assets/Icons/new_import_userChat.svg";
import importUserChatIconWhite from "@/assets/Icons/new_import_userChat_white.svg";
import parameterIcon from "@/assets/Icons/new_parameter.svg";
import requestIcon from "@/assets/Icons/new_list_square.png";
import responseIcon from "@/assets/Icons/new_response.png";
import basicInfoIcon from "@/assets/Icons/new_ui_link.svg";
import expirationIcon from "@/assets/Icons/new_solid_time.svg";
import settingIcon from "@/assets/Icons/new_setting.svg";
import SectionHeading from "@/components/SectionHeading.vue";
export default {
  mixins: [LogTrace],
  inject: ["flagNav","FontColorTitle","SubNavbarBackGround"],
  name: "SettingExternalConnector",
  components: {
    PrismEditor,
    SectionHeading,
  },
  data() {
    return {
      code: "",
      lineNumbers: true,
      expiryDate: "",
      rememberMe: false,
      dialog: false,
      loading: false,
      forgotpassword: false,
      erroralert: false,
      successalert: false,
      successMessage: "",
      errorMessage: "",
      searchTokenName: null,
      show1: false,
      Email: "",
      images: {
        logo: logoImg,
        BG: bgImg,
      },
      sectionIcons: {
        parameter: parameterIcon,
        request: requestIcon,
        response: responseIcon,
        basicInfo: basicInfoIcon,
        expiration: expirationIcon,
        setting: settingIcon,
      },
      today: null,
      tab: 0,
      yourUsername: "yourUsername",
      yourPassword: "yourPassword",
      DDList: [
        { title: "Renew", menu: 1 },
        { title: "Revork", menu: 2 },
      ],
      showToken: false,
      selectVersion: "v0",
      versions: [
        { title: "Versioned API v0", value: "v0" },
        { title: "Versioned API v1", value: "v1" },
      ],
      items: [
        // Token Management
        {
          tab: "Token Management",
          icon: {black: tokenIcon, white: tokenIconWhite},
          dataResponse: [],
        },
        // Snippet
        {
          tab: "Snippet",
          icon: {black: snippetIcon, white: snippetIconWhite},
          example: [
            {
              // Scrip snippet
              contentEx: "Example:HTML snippet",
              codeEx:
                "<!DOCTYPE html>\n" +
                '<html lang="en">\n' +
                "  <head>\n" +
                '    <script src="' +
                import.meta.env.VITE_APP_API +
                'Snippet/ChatFrameworkSnippet.js"></' +
                "script>\n" +
                '    <meta charset="UTF-8">\n' +
                '    <link rel="icon" href="/logo.png" type="image/x-icon">\n' +
                '    <meta name="viewport" content="width=device-width, initial-scale=1.0">\n' +
                "    <title>Chat Framework</title>\n" +
                "  </head" +
                "" +
                ">\n" +
                "</html>",
            },
            {
              // Scrip FullStream
              contentEx: "Example:HTML snippet FullStream",
              codeEx:
                "<!DOCTYPE html>\n" +
                '<html lang="en">\n' +
                "  <head>\n" +
                '    <script src="' +
                import.meta.env.VITE_APP_API +
                'Snippet/ChatFrameworkSnippetFull.js"></' +
                "script>\n" +
                '    <meta charset="UTF-8">\n' +
                '    <link rel="icon" href="/logo.png" type="image/x-icon">\n' +
                '    <meta name="viewport" content="width=device-width, initial-scale=1.0">\n' +
                "    <title>Chat Framework</title>\n" +
                "  </head" +
                "" +
                ">\n" +
                "</html>",
            },
          ],
          scriptAndParam: [
            {
              content: "Script",
              readonly: true,
              script:
                '<script src="' +
                import.meta.env.VITE_APP_API +
                'Snippet/ChatFrameworkSnippet.js"></' +
                "script>\n",
            },
            {
              content: "Script FullStream",
              readonly: true,
              script:
                '<script src="' +
                import.meta.env.VITE_APP_API +
                'Snippet/ChatFrameworkSnippetFull.js"></' +
                "script>\n",
            },
          ],
        },
        // Login Ext
        {
          tab: "Login External",
          icon: {black: loginExternalIcon, white: loginExternalIconWhite},
          example: [
            {
              // AXIOS
              contentEx: "Example:axios",
              codeEx: `let _data = {
                ${
                  import.meta.env.VITE_APP_ProductAdmin == "true"
                    ? '"companycode":"",'
                    : ""
                }
                "userName": "yourUsername",
                "password": "yourPassword"
              } 
              let responseData = {
                "userId": 0,
                "username": "",
                "fullName": "",
                "passwordUpdateDate": null,
                "memberId": 0,
                "roleId": 0,
                "activeFlag": true,
                "userType": null,
                "token": "",
                "email": null
              }
              axios
                .post(
                  "${
                    import.meta.env.VITE_APP_API
                  }api/v0/ExternalAPI/Login",_data
                )
                .then(function(res) {
                  let resData = res.data;
                  responseData = resData;
                })
                .catch(function(error) {
                  console.log(error);
                });`,
            },
          ],
          scriptAndParam: [
            {
              content: "URL",
              readonly: true,
              script: import.meta.env.VITE_APP_API + "api/v0/ExternalAPI/Login",
            },
            {
              content: "Username",
              readonly: false,
              script: "yourUsername",
              dataType: "Data type: string",
            },
            {
              content: "Password",
              readonly: false,
              script: "yourPassword",
              dataType: "Data type: string",
            },
          ],
          responseDescription: [
            {
              key: "userId",
              type: "int",
              description: "ID ผู้ใช้งาน",
              ex: "1",
            },
            {
              key: "username",
              type: "int",
              description: "ชื่อผู้ใช้",
              ex: "testUser",
            },
            {
              key: "fullName",
              type: "int",
              description: "ชื่อจริง",
              ex: "John Stone",
            },
            {
              key: "passwordUpdateDate",
              type: "string",
              description: "วันที่แก้ไขรหัสผ่าน",
              ex: "null",
            },
            {
              key: "memberId",
              type: "int",
              description: "ID ผู้ใช้งาน",
              ex: "1",
            },
            {
              key: "roleId",
              type: "int",
              description: "รหัสตำแหน่ง",
              ex: "1",
            },
            {
              key: "activeFlag",
              type: "bool",
              description: "สถานะการใช้งาน",
              ex: "true",
            },
            {
              key: "token",
              type: "string",
              description: "ไว้สำหรับอนุมัติเข้าใช้งาน API อื่นๆ",
              ex: "eyJhbGciOiJIUzI1N...",
            },
            {
              key: "email",
              type: "string",
              description: "John@Stone.com",
              ex: "John@Stone.com",
            },
          ],
        },
        // Vector Insert (for external)
        {
          tab: "Vector Insert",
          icon: {black: vectorIcon, white: vectorIconWhite},
          example: [
            {
              // AXIOS
              contentEx: "Example:axios",
              codeEx: `let _data = {
  "vectordbName": "test",
  "text": "รายละเอียด",
  "createFlag": true
}
axios
  .post(
    "${import.meta.env.VITE_APP_API}api/v0/ExternalAPI/CreateVector",_data,{
      headers:{
          Authorization: "Bearer yourToken",
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`,
            },
          ],
          scriptAndParam: [
            {
              content: "URL",
              readonly: true,
              script:
                import.meta.env.VITE_APP_API +
                "api/v0/ExternalAPI/CreateVector",
            },
            {
              content: "vectordbName",
              readonly: false,
              script: "test",
              dataType: "Data type: string",
            },
            {
              content: "text",
              readonly: false,
              script: "รายละเอียด",
              dataType: "Data type: string",
            },
            {
              content: "createFlag",
              readonly: false,
              script: "true",
              dataType: "Data type: bool",
            },
            {
              content: "Token",
              readonly: false,
              script: "yourToken",
              dataType: "Data type: string",
              // tooltip: "นำ Token ที่ได้จากการใช้ API: LoginEXT",
              tooltip: "Encrypted string used as a security key to authenticate the system or user and grant temporary access to specific external resources of APIs."
            },
          ],
          responseStatus: [
            {
              responseStatus: "200",
              responseMsg: "Successfully",
            },
          ],
        },
        // List Vector Insert (for external)
        {
          tab: "List Vector Insert",
          icon: {black: listIcon, white: listIconWhite},
          example: [
            {
              // AXIOS
              contentEx: "Example:axios",
              codeEx: `let _data = {
  "vectordbName": "test",
  "text": ["รายละเอียด1","รายละเอียด2"],
  "createFlag": true
}
axios
  .post(
    "${import.meta.env.VITE_APP_API}api/v0/ExternalAPI/CreateListVector",_data,{
      headers:{
          Authorization: "Bearer yourToken",
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`,
            },
          ],
          scriptAndParam: [
            {
              content: "URL",
              readonly: true,
              script:
                import.meta.env.VITE_APP_API +
                "api/v0/ExternalAPI/CreateListVector",
            },
            {
              content: "vectordbName",
              readonly: false,
              script: "test",
              dataType: "Data type: string",
            },
            {
              content: "text",
              readonly: false,
              script: '"รายละเอียด1","รายละเอียด2"',
              dataType: "Data type: array[string]",
            },
            {
              content: "createFlag",
              readonly: false,
              script: "true",
              dataType: "Data type: bool",
            },
            {
              content: "Token",
              readonly: false,
              script: "yourToken",
              dataType: "Data type: string",
              // tooltip: "นำ Token ที่ได้จากการใช้ API: LoginEXT",
              tooltip: "Encrypted string used as a security key to authenticate the system or user and grant temporary access to specific external resources of APIs."
            },
          ],
          responseStatus: [
            {
              responseStatus: "200",
              responseMsg: "Successfully",
            },
          ],
        },
        // SelectorModel
        {
          tab: "Selector Model",
          icon: {black: selectorIcon, white: selectorIconWhite},
          example: [
            {
              // AXIOS
              contentEx: "Example:axios",
              codeEx: `let _data = { 
  "events": [
    {
      "type": 1,
      "timestamp": 1701821530,
      "source": {
          "userId": "test"
          },
      "message": {
          "text": "1"
          }
    }
  ]
}
axios
  .post(
    "${import.meta.env.VITE_APP_API}api/v0/ExternalAPI/SelectorModel",_data,{
      headers:{
          Authorization: "Bearer yourToken",
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`,
            },
          ],
          scriptAndParam: [
            {
              content: "URL",
              readonly: true,
              script:
                import.meta.env.VITE_APP_API +
                "api/v0/ExternalAPI/SelectorModel",
            },
            {
              content: "type",
              readonly: false,
              script: "1",
              dataType: "Data type: int",
            },
            {
              content: "timestamp",
              readonly: false,
              script: "1701821530",
              dataType: "Data type: int",
            },
            {
              content: "userId",
              readonly: false,
              script: "test",
              dataType: "Data type: string",
            },
            {
              content: "text",
              readonly: false,
              script: "1",
              dataType: "Data type: string",
            },
            {
              content: "Token",
              readonly: false,
              script: "yourToken",
              dataType: "Data type: string",
              // tooltip: "นำ Token ที่ได้จากการใช้ API: LoginEXT",
              tooltip: "Encrypted string used as a security key to authenticate the system or user and grant temporary access to specific external resources of APIs."
            },
          ],
          responseDescription: [
            {
              key: "-",
              type: "string",
              description: "ข้อความตอบกลับแสดง model ที่เลือก",
              ex: "Model Changed to < 1: Azure GPT 3.5 >",
            },
          ],
        },
        // ChatPOST
        {
          tab: "Chat POST",
          icon: {black: chatPostIcon, white: chatPostIconWhite},
          example: [
            {
              // AXIOS
              contentEx: "Example:axios",
              codeEx: `let _data = { 
  "events": [
    {
      "type": 1,
      "timestamp": 1701821530,
      "source": {
          "userId": "test"
          },
      "message": {
          "text": "แนะนำเมนูอาหารไทย"
          }
    }
  ]
}
axios
  .post(
    "${import.meta.env.VITE_APP_API}api/v0/ExternalAPI/ChatPOST",_data,{
      headers:{
          Authorization: "Bearer yourToken",
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`,
            },
          ],
          scriptAndParam: [
            {
              content: "URL",
              readonly: true,
              script:
                import.meta.env.VITE_APP_API + "api/v0/ExternalAPI/ChatPOST",
            },
            {
              content: "type",
              readonly: false,
              script: "1",
              dataType: "Data type: int",
            },
            {
              content: "timestamp",
              readonly: false,
              script: "1701821530",
              dataType: "Data type: int",
            },
            {
              content: "userId",
              readonly: false,
              script: "test",
              dataType: "Data type: string",
            },
            {
              content: "text",
              readonly: false,
              script: "แนะนำเมนูอาหารไทย",
              dataType: "Data type: string",
            },
            {
              content: "Token",
              readonly: false,
              script: "yourToken",
              dataType: "Data type: string",
              // tooltip: "นำ Token ที่ได้จากการใช้ API: LoginEXT",
              tooltip: "Encrypted string used as a security key to authenticate the system or user and grant temporary access to specific external resources of APIs."
            },
          ],
          responseDescription: [
            {
              key: "-",
              type: "string",
              description: "ข้อความตอบกลับจากคำถามที่ได้ส่งไป",
              ex: `ผัดไทย - อาหารที่เรียกว่า “สุดยอดของความเป็นไทย” ผัดไทยเป็นอาหารที่มีรสชาติที่ซับซ้อนและสมดุลย์ดี มีรสหวาน, เปรี้ยว, ความเผ็ดและเค็มที่สมดุลย์ดี`,
            },
          ],
        },
        // ChatPromptPOST
        {
          tab: "Chat PromptPOST",
          icon: {black: chatPromptIcon, white: chatPromptIconWhite},
          example: [
            {
              // AXIOS
              contentEx: "Example:axios",
              codeEx: `let _data = { 
  "events": [
    {
      "type": 1,
      "timestamp": 1701821530,
      "source": {
          "userId": "test"
          },
      "message": {
          "text": "แนะนำร้านอาหารทะเล",
          "prompttext": "Answer the question using context below. ร้านอาหารที่ดีในกรุงเทพฯ ได้แก่ Le Normandie, Nahm, Gaggan, Eat Me, The Local, Sirocco, Bo.lan, Issaya Siamese Club, ร้านยำที่แนะนำในกรุงเทพฯมีหลายร้าน ได้แก่ ยำวังหลัง, ยำสายไหม, ยำรสเด็ด, ยำแซ่บนัว, ยำครกเพชร, ยำ2นิ้ว และยำแซ่บเวอร์ โดยทุกร้านมีเมนูยำที่หลากหลายและรสชาติเป็นเอกลักษณ์. ร้านอาหารทะเลแถวบางขุนเทียน ร้านทะเลเผา พี่น้อง, ร้านปูเป็น, ร้านเจ๊เนืองซีฟู้ด, ร้านสมพงษ์ซีฟู้ด และ ร้านเจ๊ไข่ซีฟู้ด ซึ่งเป็นร้านที่เชี่ยวชาญในอาหารทะเลและได้รับความนิยมในหมู่ชาวบางขุนเทียน. #context "
          }
    }
  ]
}
axios
  .post(
    "${import.meta.env.VITE_APP_API}api/v0/ExternalAPI/ChatPromptPOST",_data,{
      headers:{
          Authorization: "Bearer yourToken",
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`,
            },
          ],
          scriptAndParam: [
            {
              content: "URL",
              readonly: true,
              script:
                import.meta.env.VITE_APP_API +
                "api/v0/ExternalAPI/ChatPromptPOST",
            },
            {
              content: "type",
              readonly: false,
              script: "1",
              dataType: "Data type: int",
            },
            {
              content: "timestamp",
              readonly: false,
              script: "1701821530",
              dataType: "Data type: int",
            },
            {
              content: "userId",
              readonly: false,
              script: "test",
              dataType: "Data type: string",
            },
            {
              content: "text",
              readonly: false,
              script: "แนะนำร้านอาหารทะเล",
              dataType: "Data type: string",
            },
            {
              content: "prompt text",
              readonly: false,
              script:
                "Answer the question using context below. ร้านอาหารที่ดีในกรุงเทพฯ ได้แก่ Le Normandie, Nahm, Gaggan, Eat Me, The Local, Sirocco, Bo.lan, Issaya Siamese Club, ร้านยำที่แนะนำในกรุงเทพฯมีหลายร้าน ได้แก่ ยำวังหลัง, ยำสายไหม, ยำรสเด็ด, ยำแซ่บนัว, ยำครกเพชร, ยำ2นิ้ว และยำแซ่บเวอร์ โดยทุกร้านมีเมนูยำที่หลากหลายและรสชาติเป็นเอกลักษณ์. ร้านอาหารทะเลแถวบางขุนเทียน ร้านทะเลเผา พี่น้อง, ร้านปูเป็น, ร้านเจ๊เนืองซีฟู้ด, ร้านสมพงษ์ซีฟู้ด และ ร้านเจ๊ไข่ซีฟู้ด ซึ่งเป็นร้านที่เชี่ยวชาญในอาหารทะเลและได้รับความนิยมในหมู่ชาวบางขุนเทียน. #context",
              dataType: "Data type: string",
            },
            {
              content: "Token",
              readonly: false,
              script: "yourToken",
              dataType: "Data type: string",
              // tooltip: "นำ Token ที่ได้จากการใช้ API: LoginEXT",
              tooltip: "Encrypted string used as a security key to authenticate the system or user and grant temporary access to specific external resources of APIs."
            },
          ],
          responseDescription: [
            {
              key: "-",
              type: "string",
              description:
                "ข้อความตอบกลับจะแสดงผลตามที่ได้ใช้ promttext ที่เลือก",
              ex: `ร้านอาหารทะเลแถวบางขุนเทียน มี ร้านทะเลเผา พี่น้อง, ร้านปูเป็น, ร้านเจ๊เนืองซีฟู้ด, ร้านสมพงษ์ซีฟู้ด และ ร้านเจ๊ไข่ซีฟู้ด ซึ่งเป็นร้านที่เชี่ยวชาญในอาหารทะเลและได้รับความนิยมในหมู่ชาวบางขุนเทียน.`,
            },
          ],
        },
        // ImportUser (for external)
        {
          tab: "Import User",
          icon: {black: importUserIcon, white: importUserIconWhite},
          example: [
            {
              // AXIOS
              contentEx: "Example:axios",
              codeEx: `let _data = [
  {
    "username": "john",
    "passwrod": "12345678",
    "firstName": "john",
    "lastName": "due",
    "email": "john@due.com",
    "mobile": "0800000000",
    "modelID": "[1,2,3,4]",
    "roleId": 1,
  },
]
axios
  .post(
    "${import.meta.env.VITE_APP_API}api/v0/ExternalAPI/ImportUser",_data,{
      headers:{
          Authorization: "Bearer yourToken",
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`,
            },
          ],
          scriptAndParam: [
            {
              content: "URL",
              readonly: true,
              script:
                import.meta.env.VITE_APP_API + "api/v0/ExternalAPI/ImportUser",
            },
            {
              content: "username",
              readonly: false,
              script: "john",
              dataType: "Data type: string",
            },
            {
              content: "passwrod",
              readonly: false,
              script: "12345678",
              dataType: "Data type: string",
            },
            {
              content: "firstName",
              readonly: false,
              script: "john",
              dataType: "Data type: string",
            },
            {
              content: "lastName",
              readonly: false,
              script: "due",
              dataType: "Data type: string",
            },
            {
              content: "email",
              readonly: false,
              script: "john@due.com",
              dataType: "Data type: string",
            },
            {
              content: "mobile",
              readonly: false,
              script: "0800000000",
              dataType: "Data type: string",
            },
            {
              content: "modelID",
              readonly: false,
              script: "[1,2,3,4]",
              dataType: "Data type: string",
              // tooltip:
              //   "หากต้องการใช้ ModelID ให้ใช้ตามตัวอย่าง [1,2,3,4] และสามารถใช้ได้เฉพาะ Role User เท่านั้น",
              tooltip: "Model Id of role user"
            },
            {
              content: "roleId",
              readonly: false,
              script: "1",
              dataType: "Data type: int",
              // tooltip: "RoleID:1 (Admin), RoleID:2 (User)",
              tooltip: "RoleId 1 : Admin, RoleId 2 : User",
            },
            {
              content: "Token",
              readonly: false,
              script: "yourToken",
              dataType: "Data type: string",
              // tooltip: "นำ Token ที่ได้จากการใช้ API: LoginEXT",
              tooltip: "Encrypted string used as a security key to authenticate the system or user and grant temporary access to specific external resources of APIs."
            },
          ],
          responseStatus: [
            {
              responseStatus: "200",
              responseMsg: `Successfully`,
            },
          ],
          responseDescription: [
            {
              key: "mesDupUser",
              type: "array[string]",
              description: "Username ถูกใช้งานแล้ว",
              ex: '["john"]',
            },
            {
              key: "mesDupEmail",
              type: "array[string]",
              description: "Email ถูกใช้งานแล้ว",
              ex: '["john@due"]',
            },
            {
              key: "mesEmailNotFormat",
              type: "array[string]",
              description: "Email ไม่ถูกต้อง",
              ex: '["john"]',
            },
            {
              key: "mesFillAll",
              type: "array[string]",
              description: "กรุณากรอกข้อมูลให้ครบถ้วน",
              ex: '["Username:john"]',
            },
            {
              key: "mesModelNotFormat",
              type: "array[string]",
              description:
                "Model ไม่ถูกต้อง ตัวอย่างการใช้งานที่ถูกต้อง [1,2,3]",
              ex: '["[1-3,5]"]',
            },
            {
              key: "mesWarningSuperAdmin",
              type: "array[string]",
              description:
                "ข้อความแสดงถึงการตั้งค่า RoleID ผิด(ควรตั้งเป็น 1(Admin),2(User))",
              ex: '["john"]',
            },
            {
              key: "mesWrongRoleID",
              type: "array[string]",
              description: "RoleID ไม่ถูกต้อง (ควรเป็น 1=Admin หรือ 2=User)",
              ex: '["john"]',
            },
          ],
        },
        // ImportUserChat (for external)
        {
          tab: "Import UserChat",
          icon: {black: importUserChatIcon, white: importUserChatIconWhite},
          example: [
            {
              // AXIOS
              contentEx: "Example:axios",
              codeEx: `let _data = [
  {
    "username": "john",
    "passwrod": "12345678",
    "firstName": "john",
    "lastName": "due",
    "email": "john@due.com",
    "mobile": "0800000000",
    "modelID": "[1,2,3,4]",
    "socialUserID": "YourSocialUserID(From line)",
    "groupModelName": "yourGroupModelName",
    "modelQuickGroupName": "yourModelQuickGroupName"
  },
]
axios
  .post(
    "${import.meta.env.VITE_APP_API}api/v0/ExternalAPI/ImportUserChat",_data,{
      headers:{
          Authorization: "Bearer yourToken",
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`,
            },
          ],
          scriptAndParam: [
            {
              content: "URL",
              readonly: true,
              script:
                import.meta.env.VITE_APP_API +
                "api/v0/ExternalAPI/ImportUserChat",
            },
            {
              content: "username",
              readonly: false,
              script: "john",
              dataType: "Data type: string",
            },
            {
              content: "passwrod",
              readonly: false,
              script: "12345678",
              dataType: "Data type: string",
            },
            {
              content: "firstName",
              readonly: false,
              script: "john",
              dataType: "Data type: string",
            },
            {
              content: "lastName",
              readonly: false,
              script: "due",
              dataType: "Data type: string",
            },
            {
              content: "email",
              readonly: false,
              script: "john@due.com",
              dataType: "Data type: string",
            },
            {
              content: "mobile",
              readonly: false,
              script: "0800000000",
              dataType: "Data type: string",
            },
            {
              content: "modelID",
              readonly: false,
              script: "[1,2,3,4]",
              dataType: "Data type: string",
              // tooltip: "หากต้องการใช้ ModelID ให้ใช้ตามตัวอย่าง [1,2,3,4]",
              tooltip: "Model Id of role user"
            },
            {
              content: "socialUserID",
              readonly: false,
              script: "YourSocialUserID(From line)",
              dataType: "Data type: string",
              // tooltip:
              //   "พิมพ์ข้อความ :userid บน Line Chatbot เพื่อรับ socialUserID",
              tooltip: "Social Id of user"
            },
            {
              content: "groupModelName",
              readonly: false,
              script: "yourGroupModelName",
              dataType: "Data type: string",
            },
            {
              content: "modelQuickGroupName",
              readonly: false,
              script: "yourModelQuickGroupName",
              dataType: "Data type: string",
            },
            {
              content: "Token",
              readonly: false,
              script: "yourToken",
              dataType: "Data type: string",
              // tooltip: "นำ Token ที่ได้จากการใช้ API: LoginEXT",
              tooltip: "Encrypted string used as a security key to authenticate the system or user and grant temporary access to specific external resources of APIs."
            },
          ],
          responseStatus: [
            {
              responseStatus: "200",
              responseMsg: `Successfully`,
            },
          ],
          responseDescription: [
            {
              key: "mesDupUser",
              type: "array[string]",
              description: "Username ถูกใช้งานแล้ว",
              ex: '["john"]',
            },
            {
              key: "mesDupEmail",
              type: "array[string]",
              description: "Email ถูกใช้งานแล้ว",
              ex: '["john@due"]',
            },
            {
              key: "mesEmailNotFormat",
              type: "array[string]",
              description: "Email ไม่ถูกต้อง",
              ex: '["john"]',
            },
            {
              key: "mesEmailFormat",
              type: "array[string]",
              description: "รูปแบบ Email ไม่ถูกต้อง",
              ex: '["invalid@email"]',
            },
            {
              key: "mesFillAll",
              type: "array[string]",
              description: "กรุณากรอกข้อมูลให้ครบถ้วน",
              ex: '["Username:john"]',
            },
            {
              key: "mesMissingFields",
              type: "array[string]",
              description: "ฟิลด์ที่จำเป็นหายไป",
              ex: '["Missing required fields: Username, Email"]',
            },
            {
              key: "mesModelNotFormat",
              type: "array[string]",
              description:
                "Model ไม่ถูกต้อง ตัวอย่างการใช้งานที่ถูกต้อง [1,2,3]",
              ex: '["[1-3,5]"]',
            },
            {
              key: "mesGroupModelNotFound",
              type: "array[string]",
              description: "ไม่พบ GroupModelName",
              ex: '["GroupModelName not found: MyGroup"]',
            },
            {
              key: "mesModelQuickGroupNotFound",
              type: "array[string]",
              description: "ไม่พบ ModelQuickGroupName",
              ex: '["ModelQuickGroupName not found: MyQuickGroup"]',
            },
            {
              key: "mesDupSocialUserID",
              type: "array[string]",
              description: "SocialUserID ถูกใช้งานแล้ว",
              ex: '["SocialID"]',
            },
            {
              key: "mesDupRecipientId",
              type: "array[string]",
              description: "RecipientId ถูกใช้งานแล้ว",
              ex: '["FB123456"]',
            },
            {
              key: "mesDupRecipientId_Instagram",
              type: "array[string]",
              description: "RecipientId Instagram ถูกใช้งานแล้ว",
              ex: '["IG123456"]',
            },
          ],
        },
      ],
      myClipboard: "",
      urlApi: import.meta.env.VITE_APP_API,
      dialogShowToken: false,
      editedIndex: null,
      editedItem: null,
      token: null,
      tokenName: null,
      email: null,
      dialogActions: false,
      dialogRenew: false,
      dialogRevoke: false,
      EndDate: "",
      radios: "",
      nowDate: new Date().toISOString().slice(0, 10),
      date: new Date(),
      // dateFormatted: vm.formatDate((new Date(Date.now() - (new Date()).getTimezoneOffset() * 60000)).toISOString().substr(0, 10)),
      menuCustomeDate: false,
      ListDate: [
        { title: "30 Days", value: 30 },
        { title: "60 Days", value: 60 },
        { title: "90 Days", value: 90 },
        { title: "120 Days", value: 120 },
      ],
      DateSelected: 30,
      endDate: null,
      showPass: false,
      flagAdd: false,
      addItem: {
        tokenName: "",
        eMail: "",
        endDate: "",
        apI_Key: "",
      },
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
      searchTotalTable: 0,
      itemsPerPage: 10,
      totalTable: 0,
      isFirst: true,
      ProductAdminFlag: false,
      id: 0,
      rules: {
        required: (value) => !!value || this.$t('Required'),
      },
      DDGPTModel: [],
      DDGroupModel: [],
      GPTModel: null,
      GroupModel: null,
      showSuccess: false,
      SuccessMessage: "",
      showWarningDialog: false,
      warningTitle: "",
      warningMessage: "",
      showButton: true,
      deleting: false,
      endPickerYear: "",
      endPickerMonth: "",
      years: Array.from({ length: 3 }, (_, i) => new Date().getFullYear() + i),
    };
  },
  async created() {
    this.ProductAdminFlag = import.meta.env.VITE_APP_ProductAdmin === "true";
  },
  mounted: function () {
    this.GetDate();
    this.GetDDGPTModel();
    this.GetDDGroupModel();
  },
  computed: {
    headers() {
      return [
        { title: this.$t('Key'), align: 'start', sortable: false, value: 'key' },
        { title: this.$t('description'), align: 'start', value: 'description' },
        { title: this.$t('type'), align: 'start', value: 'type' },
        { title: this.$t('Example'), align: 'start', value: 'ex' },
      ];
    },
    headers2() {
      return [
        { title: this.$t('no'), align: 'start', sortable: false, value: 'no' },
        { title: this.$t('Token Name'), align: 'start', sortable: true, value: 'tokenName' },
        { title: this.$t('email'), align: 'start', sortable: true, value: 'eMail' },
        { title: this.$t('End Date'), align: 'start', sortable: true, value: 'endDataShow' },
        { title: this.$t('Token'), align: 'center', width: '300px', sortable: false, value: 'apI_Key' },
        { title: this.$t('Status'), align: 'center', sortable: false, value: 'status' },
        { title: this.$t('manage'), align: 'center', sortable: false, value: 'actions' },
      ];
    },
    getEndDate() {
      let endDate = new Date(
        this.date.getFullYear() + 1,
        this.date.getMonth(),
        this.date.getDate() + 1
      );
      return endDate.toISOString().slice(0, 10);
    },
  },
  watch: {
    options: {
      handler() {
        this.GetAPIData();
      },
      deep: true,
      immediate: true,
    },
    tab: {
      handler(val) {
        if (val == 3 || val == 6 || val == 7) {
          // Vector Insert || CHATPOST || ChatPromptPOST
          this.versions = [
            { title: "Versioned API v0", value: "v0" },
            { title: "Versioned API v1", value: "v1" },
            { title: "Versioned API v2", value: "v2" },
            { title: "Versioned API v3", value: "v3" },
          ];
        } else if (val == 5) {
          if (this.selectVersion == "v2") {
            this.selectVersion = "v1";
          }
          this.versions = [
            { title: "Versioned API v0", value: "v0" },
            { title: "Versioned API v1", value: "v1" },
            { title: "Versioned API v3", value: "v3" },
          ];
        } else {
          if (this.selectVersion == "v2" || this.selectVersion == "v3") {
            this.selectVersion = "v1";
          }
          this.versions = [
            { title: "Versioned API v0", value: "v0" },
            { title: "Versioned API v1", value: "v1" },
          ];
        }
      },
    },
    menuCustomeDate(newVal) {
      if (newVal) {
        if (this.EndDate) {
          const date = new Date(this.EndDate);
          this.endPickerYear = date.getFullYear();
          this.endPickerMonth = date.getMonth();
        } else {
          const today = new Date();
          this.endPickerYear = today.getFullYear();
          this.endPickerMonth = today.getMonth();
        }
      }
    },
  },
  methods: {
    openWarningDialog(title, message, show = true, sec = 3000) {
      this.warningTitle = title;
      this.warningMessage = message;
      this.showWarningDialog = true;
      this.showButton = show;
      if(!show){
        setTimeout(() => {
          this.closeWarningDialog();
        }, sec);
      }
    },
    closeWarningDialog() {
      this.showWarningDialog = false;
      this.warningTitle = "";
      this.warningMessage = "";
    },
    SearchTokenName() {
      this.isSearch = true;
      this.options.page = 1;
      this.GetAPIData();
    },
    validateEmail(email) {
      // eslint-disable-next-line no-useless-escape
      let re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; //NOSONAR

      return re.test(email) || this.$t('invalid email');
    },
    async GetAPIData() {
      try {
        this.dialog = true;

        let { page, itemsPerPage, sortBy } = this.options;
        let sortField = null;
        let sortOrder = null;

        if (sortBy && sortBy.length > 0) {
          sortField = sortBy[0].key;
          sortOrder = sortBy[0].order === "desc";
        }

        let temp = {
          page: page,
          perPage: itemsPerPage == -1 ? this.totalTable : itemsPerPage,
          sortDesc: sortOrder,
          sortBy: sortField,
          tokenName: this.searchTokenName != null && this.searchTokenName.trim() !== "" ? this.searchTokenName.trim() : null,
        };
        let start = (page - 1) * itemsPerPage;
        await this.$call.GetAPITokenData(
          temp,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              this.items[0].dataResponse = dataResponse.data.data.map(
                (items, index) => ({
                  ...items,
                  endDate: this.convertDateToStringFormatDatePick(
                    items.endDate
                  ),
                  endDataShow: this.convertDateToString(items.endDate),
                  show_status: false,
                  copy_status: false,
                  no: start + (index + 1),
                })
              );
              this.totalTable = this.isFirst
                ? dataResponse.data.total
                : this.totalTable;
              this.searchTotalTable = dataResponse.data.total;
            } else {
              this.items[0].dataResponse = [];
              this.totalTable = 0;
              this.searchTotalTable = 0;
            }
            this.dialog = false;
            this.isFirst = false;
          },
          (errorResource) => {
            this.dialog = false;
            if (errorResource.status != 0) {
              console.error(errorResource);
            }
          }
        );
      } catch (error) { //NOSONAR
        // error intentionally ignored
      }
    },

    convertDateToStringFormatDatePick(date, seperator = "-") {
      return dateUtils.getStringFromDatePickFormatDatePick2(
        moment(date).toDate(),
        seperator
      );
    },
    convertDateToString(date, seperator = "-") {
      return dateUtils.getStringFromDate(moment(date).toDate(), seperator);
    },
    displayActions(item, index) {
      this.flagAdd = false;
      this.editedIndex = this.items[0].dataResponse.indexOf(item);
      this.editedItem = { ...item };
      this.token = this.editedItem.apI_Key;
      this.tokenName = this.editedItem.tokenName;
      this.id = this.editedItem.id;
      this.DateSelected = 30;
      if (index === 0) {
        this.radios = "CustomeSelectDate";
        this.email = this.editedItem.eMail;
        this.EndDate = this.editedItem.endDate;
        this.GPTModel = this.editedItem.gptModelID;
        this.GroupModel = this.editedItem.groupModelID;
        this.dialogRenew = true;
      } else if (index === 1) {
        this.dialogRevoke = true;
      }
    },
    Add() {
      this.flagAdd = true;
      this.radios = "CustomeSelectDate";
      this.dialogRenew = true;

      this.tokenName = "";
      this.email = "";
      this.EndDate = this.nowDate;
      this.addItem.endDate = this.nowDate;
      this.GPTModel = null;
      this.GroupModel = null;
    },

    // Shared implementation for CreateNewAPIToken/Renew (identical flow; they differ only by
    // the $call method, an extra `id` payload field for renew, and the success toast text).
    async _saveApiToken(callFn, extraPayload, successMsg) {
      try {
        let emailvali = this.validateEmail(this.email);
        if (
          this.tokenName != null &&
          this.email != null &&
          this.tokenName != "" &&
          this.email != "" &&
          this.tokenName != undefined &&
          this.email != undefined &&
          emailvali == true //NOSONAR
        ) {
          if (this.GPTModel == null && this.GroupModel == null) {
            this.openWarningDialog(
              this.$t("warning"),
              this.$t('_PleaseSelectModelOrGroup'),
              false,
              1800
            );
            return;
          }
          this.dialog = true;
          let EndDate =
            this.radios == "CustomeSelectDate"
              ? this.EndDate
              : this.convertDateToStringFormatDatePick(
                  new Date(
                    this.date.getFullYear(),
                    this.date.getMonth(),
                    this.date.getDate() + this.DateSelected
                  )
                );
          let temp = {
            ...extraPayload,
            tokenName: this.tokenName,
            eMail: this.email,
            endDate: EndDate,
            GPTModelID: this.GPTModel,
            GroupModelID: this.GroupModel,
          };
          await callFn(
            temp,
            (dataResponse) => {
              if (dataResponse.status == 0) {
                this.dialog = false;
                if (dataResponse.data == "Data is duplicate") {
                  this.openWarningDialog(
                    this.$t("warning"),
                    dataResponse.data,
                    false,
                    1800
                  );
                } else {
                  this.SuccessMessage = successMsg;
                  this.showSuccess = true;
                  clearTimeout(this._addToastTimer);
                  this._addToastTimer = setTimeout(() => {
                    this.showSuccess = false;
                  }, 1500);
                  this.dialogRenew = false;
                  this.GetAPIData();
                }
              } else {
                this.dialogRenew = false;
                this.dialog = false;
              }
            },
            (errorResource) => {
              this.dialog = false;
              if (errorResource.status != 0) {
                console.error(errorResource);
              }
            }
          );
        } else {
          this.openWarningDialog(
            this.$t("warning"),
            this.$t("pleasefillininformation"),
            false,
            1800
          );
        }
      } catch (error) { //NOSONAR
        // error intentionally ignored
      }
    },

    async CreateNewAPIToken() {
      await this._saveApiToken(
        (temp, ok, err) => this.$call.CreateAPIToken(temp, ok, err),
        {},
        this.$t('_SuccessfullyCreated')
      );
    },

    async Renew() {
      await this._saveApiToken(
        (temp, ok, err) => this.$call.RenewAPIToken(temp, ok, err),
        { id: this.id },
        this.$t('_SuccessfullyRenewed')
      );
    },

    async Revork() {
      try {
        this.dialog = true;
        let temp = {
          id: this.id,
        };
        this.$call.RevorkAPIToken(
          temp,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              this.SuccessMessage = this.$t('_SuccessfullyRevoked');
              this.showSuccess = true;
              clearTimeout(this._addToastTimer);
              this._addToastTimer = setTimeout(() => {
                this.showSuccess = false;
              }, 1800);
              this.dialog = false;
              this.GetAPIData();
            } else {
              this.$swal.fire({
                icon: "error",
                title: dataResponse.message,
                showConfirmButton: false,
                timer: 1800,
              });
              this.dialog = false;
              this.GetAPIData();
            }
            this.dialog = false;
            this.isFirst = false;
            this.dialogRevoke = false;
          },
          (errorResource) => {
            this.dialog = false;
            if (errorResource.status != 0) {
              console.error(errorResource);
            }
          }
        );
      } catch (error) { //NOSONAR
        // error intentionally ignored
      }
    },
    // Helper: build the standard axios.post(...).then().catch() block used by
    // most external API code examples. Output is identical to the previously
    // duplicated inline template literals; extracted to reduce duplication.
    _buildAxiosBlock(endpoint, token) {
      const isV0 = this.selectVersion == "v0";
      const authKey = isV0 ? "Authorization" : "'Api-Key'";
      const authVal = isV0 ? '"Bearer ' + token + '"' : "`" + token + "`";
      return `axios
  .post(
    "${this.urlApi}api/${this.selectVersion}/ExternalAPI/${endpoint}",_data,{
      headers:{
          ${authKey}: ${authVal},
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`;
    },
    updateExampleCode(index) {
      // Login EXT
      if (index == 2) {
        const child = this.items[index].scriptAndParam;
        let UserName = child[1].script;
        let Password = child[2].script;
        const exampleItem = this.items[index].example[0];

        exampleItem.codeEx = `let _data = {
  "userName": "${UserName}",
  "password": "${Password}"
} 
let responseData = {
  "userId": 0,
  "username": "",
  "fullName": "",
  "passwordUpdateDate": null,
  "memberId": 0,
  "roleId": 0,
  "activeFlag": true,
  "userType": null,
  "token": "",
  "email": null
}
axios
  .post(
    "${this.urlApi}api/${this.versions.value}/ExternalAPI/Login",_data
  )
  .then(function(res) {
    let resData = res.data;
    responseData = resData;
  })
  .catch(function(error) {
    console.log(error);
  });`;
      }
      // Vector Insert
      else if (index == 3) {
        const child = this.items[index].scriptAndParam;
        const exampleItem = this.items[index].example[0];
        if (this.selectVersion == "v2") {
          let vectordbName = child[1].script;
          let deleteAll = child[2].script;
          let token = child[3].script;
          exampleItem.codeEx = `let _data = new FormData();
_data.append("vectordbname", "${vectordbName}");
_data.append("entity", file); // File object
_data.append("deleteAll", "${deleteAll}");

axios
  .post(
    "${this.urlApi}api/${this.selectVersion}/ExternalAPI/CreateVector",_data,{
      headers:{
          'Api-Key': \`${token}\`,
          'Content-Type': 'multipart/form-data'
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`;
        } else if (this.selectVersion == "v3") {
          let vectordbName = child[1].script;
          let fileName = child[2].script;
          let path = child[3].script;
          let deleteAll = child[4].script;
          let token = child[5].script;
          exampleItem.codeEx = `let _data = {
  "vectordbName": "${vectordbName}",
  "fileName": "${fileName}",
  "path": "${path}",
  "deleteAll": ${deleteAll}
}
axios
  .post(
    "${this.urlApi}api/${this.selectVersion}/ExternalAPI/CreateVector",_data,{
      headers:{
          'Api-Key': \`${token}\`,
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`;
        } else {
          let vectordbName = child[1].script;
          let text = child[2].script;
          let createFlag = child[3].script;
          let token = child[4].script;
          exampleItem.codeEx = `let _data = {
  "vectordbName": "${vectordbName}",
  "text": "${text}",
  "createFlag": ${createFlag}
}
${this._buildAxiosBlock("CreateVector", token)}`;
        }
      }
      // List Vector Insert
      else if (index == 4) {
        const child = this.items[index].scriptAndParam;
        let vectordbName = child[1].script;
        let text = child[2].script;
        let createFlag = child[3].script;
        let token = child[4].script;
        const exampleItem = this.items[index].example[0];
        exampleItem.codeEx = `let _data = {
  "vectordbName": "${vectordbName}",
  "text": [${text}],
  "createFlag": ${createFlag}
}
${this._buildAxiosBlock("CreateListVector", token)}`;
      }
      // Selector Model
      else if (index == 5) {
        const child = this.items[index].scriptAndParam;
        let type = child[1].script;
        let timestamp = child[2].script;
        let userId = child[3].script;
        let text = child[4].script;
        let token = child[5].script;
        const exampleItem = this.items[index].example[0];
        exampleItem.codeEx = `let _data = { 
  "events": [
    {
      "type": ${type},
      "timestamp": ${timestamp},
      "source": {
          "userId": "${userId}"
          },
      "message": {
          "text": "${text}"
          }
    }
  ]
}
${this._buildAxiosBlock("SelectorModel", token)}`;
      }
      //ChatPOST
      else if (index == 6) {
        this._buildExampleChatPost(index);
      }
      //ChatPOST
      else if (index == 7) {
        const child = this.items[index].scriptAndParam;
        let type = child[1].script;
        let timestamp = child[2].script;
        let userId = child[3].script;
        let text = child[4].script;
        let promttext = child[5].script;
        let token = child[6].script;
        const exampleItem = this.items[index].example[0];
        let parameterRequest =
          this.selectVersion != "v2" //NOSONAR
            ? `let _data = {
  "events": [
    {
      "type": ${type},
      "timestamp": ${timestamp},
      "source": {
          "userId": "${userId}"
          },
      "message": {
          "text": "${text}",
          "prompttext": "${promttext}"
          }
    }
  ]
}`
            : `let _data = new FormData();
  if (file) {
    formData.append("Image", file); // Type file image only
  }
  formData.append("userId", "${userId}");
  formData.append("text", "${text}");
  formData.append("prompttext", "${promttext}");
`;
        exampleItem.codeEx = `let _data = ${parameterRequest}
${this._buildAxiosBlock("ChatPromptPOST", token)}`;
      }
      //ImportUser
      else if (index == 8) {
        const child = this.items[index].scriptAndParam;
        let username = child[1].script;
        let passwrod = child[2].script;
        let firstName = child[3].script;
        let lastName = child[4].script;
        let email = child[5].script;
        let mobile = child[6].script;
        let modelID = child[7].script;
        let roleId = child[8].script;
        let token = child[9].script;
        const exampleItem = this.items[index].example[0];
        exampleItem.codeEx = `let _data = [
  {
    "username": "${username}",
    "passwrod": "${passwrod}",
    "firstName": "${firstName}",
    "lastName": "${lastName}",
    "email": "${email}",
    "mobile": "${mobile}",
    "modelID": "${modelID}",
    "roleId": ${roleId},
  },
]
${this._buildAxiosBlock("ImportUser", token)}`;
      }
      //ImportUserChat
      else if (index == 9) {
        const child = this.items[index].scriptAndParam;
        let username = child[1].script;
        let passwrod = child[2].script;
        let firstName = child[3].script;
        let lastName = child[4].script;
        let email = child[5].script;
        let mobile = child[6].script;
        let modelID = child[7].script;
        let socialUserID = child[8].script;
        let groupModelName = child[9].script;
        let modelQuickGroupName = child[10].script;
        let token = child[11].script;
        const exampleItem = this.items[index].example[0];
        exampleItem.codeEx = `let _data = [
  {
    "username": "${username}",
    "passwrod": "${passwrod}",
    "firstName": "${firstName}",
    "lastName": "${lastName}",
    "email": "${email}",
    "mobile": "${mobile}",
    "modelID": "${modelID}",
    "socialUserID": "${socialUserID}",
    "groupModelName": "${groupModelName}",
    "modelQuickGroupName": "${modelQuickGroupName}"
  },
]
${this._buildAxiosBlock("ImportUserChat", token)}`;
      }
    },
    // Helper: build the ChatPOST example (index 6). Behavior identical to the
    // previously inline branch; extracted to reduce cognitive complexity.
    _buildExampleChatPost(index) {
      const child = this.items[index].scriptAndParam;
      let type = child[1].script;
      let timestamp = child[2].script;
      let userId = child[3].script;
      let text = child[4].script;
      let token = child[5].script;
      const exampleItem = this.items[index].example[0];
      let parameterRequest =
        this.selectVersion != "v2" //NOSONAR
          ? `let _data = {
  "events": [
    {
      "type": ${type},
      "timestamp": ${timestamp},
      "source": {
          "userId": "${userId}"
          },
      "message": {
          "text": "${text}"
          }
    }
  ]
}`
          : `let _data = new FormData();
  if (file) {
    formData.append("Image", file); // Type file image only
  } 
  formData.append("userId", "${userId}");
  formData.append("text", "${text}");
`;
      exampleItem.codeEx = `${parameterRequest}
${this._buildAxiosBlock("ChatPOST", token)}`;
    },
    switchVersion() {
      for (let index = 3; index < this.items.length; index++) {
        // Vector Insert
        if (index == 3) {
          this._switchVersionVector(index);
        }
        // List Vector Insert
        else if (index == 4) {
          this._switchVersionListVector(index);
        }
        // Selector Model
        else if (index == 5) {
          this._switchVersionSelectorModel(index);
        }
        //ChatPOST
        else if (index == 6) {
          this._switchVersionChatPost(index);
        }
        //ChatPOST
        else if (index == 7) {
          this._switchVersionChatPromptPost(index);
        }
        //ImportUser
        else if (index == 8) {
          this._switchVersionImportUser(index);
        }
        //ImportUserChat
        else if (index == 9) {
          this._switchVersionImportUserChat(index);
        }
      }
    },
    // Helpers: each builds the scriptAndParam / example for one tab index.
    // Bodies extracted verbatim from switchVersion to reduce cognitive
    // complexity; behavior is identical.
    _switchVersionVector(index) {
      this.items[
        index
      ].scriptAndParam[0].script = `${this.urlApi}api/${this.selectVersion}/ExternalAPI/CreateVector`;

      if (this.selectVersion == "v2") {
        // v2: FormData with file upload
        this.items[index].scriptAndParam = [
          {
            content: "URL",
            readonly: true,
            script: this.urlApi + "api/v2/ExternalAPI/CreateVector",
          },
          {
            content: "vectordbname",
            readonly: false,
            script: "test",
            dataType: "Data type: string",
          },
          {
            content: "deleteAll",
            readonly: false,
            script: "false",
            dataType: "Data type: bool",
          },
          {
            content: "API-Key",
            readonly: false,
            script: "yourApiKey",
            dataType: "Data type: string",
            tooltip: "ให้นำ API-Key จาก Token management มาใช้",
          },
        ];
        const child = this.items[index].scriptAndParam;
        let vectordbName = child[1].script;
        let deleteAll = child[2].script;
        let token = child[3].script;
        const exampleItem = this.items[index].example[0];
        exampleItem.codeEx = `let _data = new FormData();
_data.append("vectordbname", "${vectordbName}");
_data.append("entity", file); // File object
_data.append("deleteAll", "${deleteAll}");

axios
  .post(
    "${this.urlApi}api/v2/ExternalAPI/CreateVector",_data,{
      headers:{
          'Api-Key': \`${token}\`,
          'Content-Type': 'multipart/form-data'
        }
    }
  )
  .then(function(res) {
    let resData = res.data;
    console.log(resData);
  })
  .catch(function(error) {
    console.log(error);
  });`;
      } else if (this.selectVersion == "v3") {
        // v3: JSON body with FileName from blob storage
        this.items[index].scriptAndParam = [
          {
            content: "URL",
            readonly: true,
            script: this.urlApi + "api/v3/ExternalAPI/CreateVector",
          },
          {
            content: "vectordbName",
            readonly: false,
            script: "test",
            dataType: "Data type: string",
          },
          {
            content: "fileName",
            readonly: false,
            script: "document.pdf",
            dataType: "Data type: string",
            tooltip: "ชื่อไฟล์ที่อยู่ใน Blob Storage",
          },
          {
            content: "path",
            readonly: false,
            script: "",
            dataType: "Data type: string",
            tooltip: "Container path ใน Blob Storage (ถ้าไม่ระบุจะใช้ค่า default)",
          },
          {
            content: "deleteAll",
            readonly: false,
            script: "false",
            dataType: "Data type: bool",
          },
          {
            content: "API-Key",
            readonly: false,
            script: "yourApiKey",
            dataType: "Data type: string",
            tooltip: "ให้นำ API-Key จาก Token management มาใช้",
          },
        ];
        const child = this.items[index].scriptAndParam;
        let vectordbName = child[1].script;
        let fileName = child[2].script;
        let path = child[3].script;
        let deleteAll = child[4].script;
        let token = child[5].script;
        const exampleItem = this.items[index].example[0];
        exampleItem.codeEx = `let _data = {
  "vectordbName": "${vectordbName}",
  "fileName": "${fileName}",
  "path": "${path}",
  "deleteAll": ${deleteAll}
}
${this._buildAxiosBlock("CreateVector", token)}`;
      } else {
        // v0/v1: JSON body with text
        const child = this.items[index].scriptAndParam;
        if (child.length < 5) {
          // Restore default params for v0/v1
          this.items[index].scriptAndParam = [
            {
              content: "URL",
              readonly: true,
              script: this.urlApi + "api/" + this.selectVersion + "/ExternalAPI/CreateVector",
            },
            {
              content: "vectordbName",
              readonly: false,
              script: "test",
              dataType: "Data type: string",
            },
            {
              content: "text",
              readonly: false,
              script: "รายละเอียด",
              dataType: "Data type: string",
            },
            {
              content: "createFlag",
              readonly: false,
              script: "true",
              dataType: "Data type: bool",
            },
            {
              content: this.selectVersion == "v0" ? "Token" : "API-Key",
              readonly: false,
              script: "yourToken",
              dataType: "Data type: string",
              tooltip: this.selectVersion == "v0"
                ? "ให้นำ Token ที่ได้จากการใช้ API: LoginEXT "
                : "ให้นำ API-Key จาก Token management มาใช้",
            },
          ];
        }
        const child2 = this.items[index].scriptAndParam;
        let vectordbName = child2[1].script;
        let text = child2[2].script;
        let createFlag = child2[3].script;
        let token = child2[4].script;
        child2[4].content = this.selectVersion == "v0" ? "Token" : "API-Key";
        child2[4].tooltip = this.selectVersion == "v0"
          ? "ให้นำ Token ที่ได้จากการใช้ API: LoginEXT "
          : "ให้นำ API-Key จาก Token management มาใช้";

        const exampleItem = this.items[index].example[0];
        exampleItem.codeEx = `let _data = {
  "vectordbName": "${vectordbName}",
  "text": "${text}",
  "createFlag": ${createFlag}
}
${this._buildAxiosBlock("CreateVector", token)}`;
      }
    },
    _switchVersionListVector(index) {
      const child = this.items[index].scriptAndParam;
      this.items[
        index
      ].scriptAndParam[0].script = `${this.urlApi}api/${this.selectVersion}/ExternalAPI/CreateListVector`;
      let vectordbName = child[1].script;
      let text = child[2].script;
      let createFlag = child[3].script;
      let token = child[4].script;
      this.items[index].scriptAndParam[4].content = `${
        this.selectVersion == "v0" ? "Token" : "API-Key"
      }`;
      this.items[index].scriptAndParam[4].tooltip = `${
        this.selectVersion == "v0"
          ? "ให้นำ Token ที่ได้จากการใช้ API: LoginEXT "
          : "ให้นำ API-Key จาก Token management มาใช้"
      }`;

      const exampleItem = this.items[index].example[0];
      exampleItem.codeEx = `let _data = {
  "vectordbName": "${vectordbName}",
  "text": [${text}],
  "createFlag": ${createFlag}
}
${this._buildAxiosBlock("CreateListVector", token)}`;
    },
    _switchVersionSelectorModel(index) {
      const child = this.items[index].scriptAndParam;
      this.items[
        index
      ].scriptAndParam[0].script = `${this.urlApi}api/${this.selectVersion}/ExternalAPI/SelectorModel`;
      let type = child[1].script;
      let timestamp = child[2].script;
      let userId = child[3].script;
      let text = child[4].script;
      let token = child[5].script;
      this.items[index].scriptAndParam[5].content = `${
        this.selectVersion == "v0" ? "Token" : "API-Key"
      }`;
      this.items[index].scriptAndParam[5].tooltip = `${
        this.selectVersion == "v0"
          ? "ให้นำ Token ที่ได้จากการใช้ API: LoginEXT "
          : "ให้นำ API-Key จาก Token management มาใช้"
      }`;

      const exampleItem = this.items[index].example[0];
      exampleItem.codeEx = `let _data = { 
  "events": [
    {
      "type": ${type},
      "timestamp": ${timestamp},
      "source": {
          "userId": "${userId}"
          },
      "message": {
          "text": "${text}"
          }
    }
  ]
}
${this._buildAxiosBlock("SelectorModel", token)}`;
    },
    _switchVersionChatPost(index) {
      const child = this.items[index].scriptAndParam;
      this.items[
        index
      ].scriptAndParam[0].script = `${this.urlApi}api/${this.selectVersion}/ExternalAPI/ChatPOST`;
      let type = child[1].script;
      let timestamp = child[2].script;
      let userId = child[3].script;
      let text = child[4].script;
      let token = child[5].script;
      this.items[index].scriptAndParam[5].content = `${
        this.selectVersion == "v0" ? "Token" : "API-Key"
      }`;
      this.items[index].scriptAndParam[5].tooltip = `${
        this.selectVersion == "v0"
          ? "ให้นำ Token ที่ได้จากการใช้ API: LoginEXT "
          : "ให้นำ API-Key จาก Token management มาใช้"
      }`;
      let parameterRequest = "";
      if (this.selectVersion != "v2" && this.selectVersion != "v3") {
        parameterRequest = `let _data = {
            "events": [
              {
                "type": ${type},
                "timestamp": ${timestamp},
                "source": {
                    "userId": "${userId}"
                    },
                "message": {
                    "text": "${text}"
                    }
              }
            ]
          }`;
      } else {
        parameterRequest = `let _data = new FormData();
          if (file) {
            formData.append("Image", file); // Type file image only
          } 
          formData.append("userId", "${userId}");
          formData.append("text", "${text}");`;
      }
      const exampleItem = this.items[index].example[0];
      exampleItem.codeEx = `${parameterRequest}
${this._buildAxiosBlock("ChatPOST", token)}`;
    },
    _switchVersionChatPromptPost(index) {
      const child = this.items[index].scriptAndParam;
      this.items[
        index
      ].scriptAndParam[0].script = `${this.urlApi}api/${this.selectVersion}/ExternalAPI/ChatPromptPOST`;
      let type = child[1].script;
      let timestamp = child[2].script;
      let userId = child[3].script;
      let text = child[4].script;
      let promttext = child[5].script;
      let token = child[6].script;
      this.items[index].scriptAndParam[6].content = `${
        this.selectVersion == "v0" ? "Token" : "API-Key"
      }`;
      this.items[index].scriptAndParam[6].tooltip = `${
        this.selectVersion == "v0"
          ? "ให้นำ Token ที่ได้จากการใช้ API: LoginEXT "
          : "ให้นำ API-Key จาก Token management มาใช้"
      }`;
      let parameterRequest = "";
      if (this.selectVersion != "v2" && this.selectVersion != "v3") {
        parameterRequest = `let _data = {
            "events": [
              {
                "type": ${type},
                "timestamp": ${timestamp},
                "source": {
                    "userId": "${userId}"
                    },
                "message": {
                    "text": "${text}",
                    "prompttext": "${promttext}"
                    }
              }
            ]
          }`;
      } else {
        parameterRequest = `let _data = new FormData();
          if (file) {
            formData.append("Image", file); // Type file image only
          } 
          formData.append("userId", "${userId}");
          formData.append("text", "${text}");
          formData.append("prompttext", "${promttext}");`;
      }

      // 'Version 0 ให้นำ Token ที่ได้จากการใช้ API: LoginEXT หากใช้ \n'+
      // 'Version 1 ให้นำ API-Key จากมาใช้แทน'

      const exampleItem = this.items[index].example[0];
      exampleItem.codeEx = `${parameterRequest}
${this._buildAxiosBlock("ChatPromptPOST", token)}`;
    },
    _switchVersionImportUser(index) {
      const child = this.items[index].scriptAndParam;
      this.items[
        index
      ].scriptAndParam[0].script = `${this.urlApi}api/${this.selectVersion}/ExternalAPI/ImportUser`;
      let username = child[1].script;
      let passwrod = child[2].script;
      let firstName = child[3].script;
      let lastName = child[4].script;
      let email = child[5].script;
      let mobile = child[6].script;
      let modelID = child[7].script;
      let roleId = child[8].script;
      let token = child[9].script;
      const exampleItem = this.items[index].example[0];
      exampleItem.codeEx = `let _data = [
  {
    "username": "${username}",
    "passwrod": "${passwrod}",
    "firstName": "${firstName}",
    "lastName": "${lastName}",
    "email": "${email}",
    "mobile": "${mobile}",
    "modelID": "${modelID}",
    "roleId": ${roleId},
  },
]
${this._buildAxiosBlock("ImportUser", token)}`;
    },
    _switchVersionImportUserChat(index) {
      const child = this.items[index].scriptAndParam;
      this.items[
        index
      ].scriptAndParam[0].script = `${this.urlApi}api/${this.selectVersion}/ExternalAPI/ImportUserChat`;
      let username = child[1].script;
      let passwrod = child[2].script;
      let firstName = child[3].script;
      let lastName = child[4].script;
      let email = child[5].script;
      let mobile = child[6].script;
      let modelID = child[7].script;
      let socialUserID = child[8].script;
      let groupModelName = child[9].script;
      let modelQuickGroupName = child[10].script;
      let token = child[11].script;
      const exampleItem = this.items[index].example[0];
      exampleItem.codeEx = `let _data = [
  {
    "username": "${username}",
    "passwrod": "${passwrod}",
    "firstName": "${firstName}",
    "lastName": "${lastName}",
    "email": "${email}",
    "mobile": "${mobile}",
    "modelID": "${modelID}",
    "socialUserID": "${socialUserID}",
    "groupModelName": "${groupModelName}",
    "modelQuickGroupName": "${modelQuickGroupName}"
  },
]
${this._buildAxiosBlock("ImportUserChat", token)}`;
    },
    handlebars(code) {
      let type = "handlebars";
      let value = `<code class="language-${type}">${highlight(
        code,
        languages[type],
        type
      )}</code>`;
      return value;
    },
    highlighter(code) {
      return highlight(code, languages.js); //returns html
    },
    copyClipboard(index, side) {
      let text = document.createElement("textarea");
      text.value =
        side == "left"
          ? this.items[this.tab].scriptAndParam[index].script
          : this.items[this.tab].example[index].codeEx;
      text.focus();
      document.body.appendChild(text);
      text.select();
      document.execCommand("copy");
      text.remove();
    },
    ClipboardAPIKey(item) {
      this.editedIndex = this.items[0].dataResponse.indexOf(item);
      let text = item.apI_Key;

      // ใช้ Clipboard API
      navigator.clipboard.writeText(text);

      // สร้าง array ใหม่เพื่อให้ Vue ตรวจจับการเปลี่ยนแปลง
      this.items[0].dataResponse = this.items[0].dataResponse.map(
        (obj, index) => {
          if (index === this.editedIndex) {
            return {
              ...obj,
              copy_status: true,
            };
          } else {
            return {
              ...obj,
              copy_status: false,
            };
          }
        }
      );

      setTimeout(() => {
        this.items[0].dataResponse = this.items[0].dataResponse.map(
          (obj, index) => {
            if (index === this.editedIndex) {
              return {
                ...obj,
                copy_status: false,
              };
            }
            return obj;
          }
        );
      }, 1800);
    },

    ShowAPIKey(item) {
      this.editedIndex = this.items[0].dataResponse.indexOf(item);

      // สร้าง array ใหม่เพื่อให้ Vue ตรวจจับการเปลี่ยนแปลง
      this.items[0].dataResponse = this.items[0].dataResponse.map(
        (obj, index) => {
          if (index === this.editedIndex) {
            return {
              ...obj,
              show_status: !obj.show_status,
              copy_status: false,
            };
          } else {
            return {
              ...obj,
              show_status: false,
              copy_status: false,
            };
          }
        }
      );
    },

    GetDDGPTModel() {
      this.dialog = true;
      this.$call.DDGPTModel(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.DDGPTModel = dataResponse.data;
          } else {
            this.DDGPTModel = [];
          }
        },
        (errorResource) => {
          this.dialog = false;
          console.error(errorResource);
        }
      );
    },
    GetDDGroupModel() {
      this.dialog = true;
      this.$call.GetDDGroupModel(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            this.DDGroupModel = dataResponse.data;
          } else {
            this.DDGroupModel = [];
          }
          this.dialog = false;
        },
        (errorResource) => {
          this.dialog = false;
          console.error(errorResource);
        }
      );
    },
  },
};
</script>
<style scoped>
  .section-heading-h {
    font-size: 18px;
    font-weight: 650;
  }
  .success-toast {
  position: fixed;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(100deg, #1D863C 0%, #135928 100%) !important;
  color: #ffffff;
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
  z-index: 9999;
  width: 50%;
  max-width: 700px;
  min-width: 320px;
  justify-content: center;
  padding: 12px;
  border: 1px solid #ffffff;
  gap: 20px;
  margin-top: 140px;
}
.tab-icon-container {
  width: 32px; 
  height: 32px; 
  border-radius: 50%; 
  display: flex; 
  align-items: center; 
  justify-content: center
}
.active_tab {
  background-color: #C32E0E;
}
.active_text {
  color: #C32E0E;
  font-weight: 600;
}
.tokenNameInput :deep(.v-input__control .v-field) {
  border-radius: 16px !important;
  background-color: white !important;
  display: flex;
  padding: 10px 18px;
  align-items: center;
  height: 45px !important;
  width: 300px;
  flex: 1 0 0;
  align-self: stretch;
}
.external-connector-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 24px;
}

/* Page Header */
.page-header {
  margin-bottom: 32px;
  margin-top: 20px;
}

/* Main Card */
.main-card {
  overflow: hidden;
}

/* Tabs */
.tabs-container {
  background: #ffffff;
  border: 1px solid #E5E7EB;
  border-radius: 16px;
  padding: 8px 10px 6px;
  margin: 0 12px;
}

.tabs-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
  scrollbar-gutter: stable;
}

.tabs-scroll::-webkit-scrollbar {
  height: 8px;
}

.tabs-scroll::-webkit-scrollbar-track {
  background: #F4F4F5;
  border-radius: 999px;
}

.tabs-scroll::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 999px;
}

.tabs-scroll::-webkit-scrollbar-thumb:hover {
  background: #9CA3AF;
}

.tabs-wrapper {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  gap: 6px;
  width: max-content;
  min-width: 100%;
}

.tab-item {
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 138px;
  flex: 0 0 auto;
}

.tab-item:hover {
  background: #FFF2F2;
  border-radius: 12px;
}

.tab-item.active {
  background: linear-gradient(135deg, #556AC4 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.tab-text {
  font-size: 0.9rem;
}

/* Tab Content */
.tab-content {
  margin-top: 24px;
}

.token-management-panel {
  padding: 0 20px;
}
.token-content {
  border-radius: 16px;
  padding: 16px 24px;
  border: 1px solid #E5E7EB;
}

.tab-panel {
  min-height: 400px;
}

/* Action Bar */
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.add-button {
  display: flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(100deg, #C32E0E 0%, #F67760 100%);
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
}

.add-button:hover {
  background: linear-gradient(100deg, #F36243 0%, #FF8E7A 100%) !important;
}

/* Table */
.table-container {
  background: white;
  border-radius: 16px;
  overflow: hidden;
}

.modern-table :deep(thead tr) {
  background: #FFF2F2;
}

.modern-table :deep(thead th) {
  color: black !important;
}
.v-table :deep(.v-data-table__th) {
  border-bottom: none !important;
}
.modern-table :deep(.v-data-table-header__content span) {
  font-weight: 600 !important;
}

.modern-table :deep(tbody tr) {
  transition: all 0.2s ease;
}

.modern-table :deep(tbody tr:hover) {
  background: #FFFCFC;
}

.modern-table :deep(tbody td) {
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
}
.v-table :deep(.mdi-menu-down.mdi.v-icon.notranslate.v-theme--light.v-icon--size-default.v-select__menu-icon) {
  color: #872612;
  opacity: 1 !important;
}

/* API Key Components */
.api-key-container {
  position: relative;
  padding: 16px 0;
}

.api-key-display {
  display: flex;
  align-items: center;
  gap: 5px;
  background: #EFF4FF;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.api-key-input {
  flex: 1;
  border: none;
  background: transparent;
  font-family: "Monaco", "Consolas", monospace;
  font-size: 0.875rem;
  color: #475569;
}

.icon-button {
  display: flex;
  justify-content: center;
  align-items: center;
}

.copy-toast {
  position: absolute;
  top: -8px;
  right: 0;
  background: #F2F5FC;
  color: #3859D0;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 10;
  animation: fadeInOut 1.8s ease-in-out;
}

@keyframes fadeInOut {
  0%,
  100% {
    opacity: 0;
    transform: translateY(4px);
  }
  10%,
  90% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Status Badge */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 18px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.status-badge.active {
  background: #F2FBF4;
  color: #1C7E39;
}

.status-badge.expired {
  background: #FEF1F1;
  color: #D11920;
}

/* Action Menu */
.action-menu-button {
  background: transparent;
  border: none;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
}

.action-menu-button:hover {
  background: #f1f5f9;
  color: #475569;
}

.action-menu {
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.action-item {
  padding: 12px 16px;
  transition: all 0.2s ease;
}

.action-item:hover {
  background: #f8fafc;
}

/* API Documentation */
.api-docs-panel {
  height: 100%;
}

.docs-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  height: 100%;
  padding-bottom: 20px;
}
.docs-left {
  margin-left: 20px;
}
.docs-right {
  margin-right: 20px;
  height: fit-content;
}
.docs-left, .docs-right {
  border-radius: 16px;
  padding: 16px 24px;
  box-shadow: rgba(0, 0, 0, 0.11) 0px 4px 22px !important;
}
.no_container {
  padding: 0;
  box-shadow: none !important;
}

.modern-response-table {
  border: 1px solid #C9C9C9;
  border-radius: 12px;
}
.modern-response-table :deep(.v-table__wrapper thead tr th) {
  background: #FFF2F2;
}
.modern-response-table :deep(.v-table__wrapper thead tr th:first-child) {
  border-radius: 12px 0 0 0;
}
.modern-response-table :deep(.v-table__wrapper thead tr th:last-child) {
  border-radius: 0 12px 0 0;
}
.modern-response-table :deep(.v-data-table-header__content span) {
  font-weight: 600;
}

.section-card {
  border-radius: 16px;
  padding: 24px;
  height: 100%;
  overflow-y: auto;
}

.section-title {
  font-size: 1rem;
  font-weight: 650;
  color: black;
  margin-bottom: 16px;
}
.docs-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2d3748;
  border-bottom: 1px solid #C9C9C9;
  padding-bottom: 12px;
}

/* Parameters */
.param_div {
  box-shadow: rgba(0, 0, 0, 0.11) 0px 4px 22px !important;
  padding: 20px;
  border-radius: 16px;
}
.importUser_div {
  box-shadow: 0 -12px 22px -2px rgba(0, 0, 0, 0.11) !important;
  border-radius: 16px 16px 0 0;
  padding-bottom: 10px;
}

.divider-title {
  font-weight: 650;
  border-bottom: 1px solid #C9C9C9;
  padding: 0 0 12px 0;
  font-size: 18px;
}

.request_div_top {
  margin-top: 24px;
  box-shadow: 0 -12px 22px -2px rgba(0, 0, 0, 0.11);
  border-radius: 16px 16px 0 0;
  padding: 20px 20px 10px;
}
.request_div_top_importUser {
  margin-top: 0;
  box-shadow:
    -4px 0 6px -5px rgba(0, 0, 0, 0.12),
     4px 0 6px -5px rgba(0, 0, 0, 0.12);
  border-radius: 0;
  padding: 0 20px 10px;
}
.request_div_middle {
  box-shadow:
    -4px 0 6px -5px rgba(0, 0, 0, 0.12),
     4px 0 6px -5px rgba(0, 0, 0, 0.12);
  padding: 0 20px 10px;
}
.request_div_bottom {
  box-shadow: 0 12px 22px -2px rgba(0, 0, 0, 0.11);
  border-radius: 0 0 16px 16px;
  padding: 0 20px 20px;
}

.param-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.param-name {
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.param-type {
  background: #F2F5FC;
  color: #3859D0;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 550;
  font-family: "Monaco", "Consolas", monospace;
}

.info-icon {
  color: #64748b;
  cursor: help;
  margin-top: 1px;
  flex-shrink: 0;
}

.param-input {
  position: relative;
}

.modern-textarea {
  width: 100%;
  padding: 12px 30px 12px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  font-family: "Monaco", "Consolas", monospace;
  font-size: 0.875rem;
  background: white;
  resize: vertical;
  min-height: 80px !important;
}

.modern-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.copy-param-button {
  position: absolute;
  top: 20px;
  right: 15px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
}

/* Response Section */
.response-section {
  margin-top: 24px;
  padding: 20px;
  box-shadow: rgba(0, 0, 0, 0.11) 0px 4px 22px !important;
  border-radius: 16px;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0 16px;
  background: white;
  border-radius: 8px;
}

.status-code {
  background: #F2FBF4;
  color: #1C7E39;
  padding: 4px 12px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
}

.warning-card {
  border-radius: 12px !important;
}

.status-message {
  font-family: "Monaco", "Consolas", monospace;
  color: #475569;
}

/* Code Examples */
.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.version-select {
  max-width: 200px;
}

.code-example {
  margin-bottom: 8px;
}

.code-label {
  font-size: 0.875rem;
  margin-bottom: 8px;
  font-weight: 500;
}

.code-container {
  position: relative;
  background: #1a202c;
  border-radius: 12px;
  overflow: hidden;
}

.modern-code-editor {
  font-family: "Monaco", "Consolas", monospace;
  font-size: 0.875rem;
  line-height: 1.6;
}

.copy-code-button {
  position: absolute;
  top: 2px;
  right: 2px;
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

/* Dialog Base */
.header-icon {
  margin-right: 16px;
  background: white;
  padding: 12px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.header-content {
  flex: 1;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  color: #64748b;
}

.header-text-content {
  flex: 1;
  min-width: 0;
}

.header-actions {
  flex-shrink: 0;
  margin-left: 16px;
}

.usage-info {
  margin-bottom: 24px;
}

.info-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 16px;
}

.inline-code {
  background: #1e293b;
  color: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: "Monaco", "Consolas", monospace;
  font-size: 0.8125rem;
}

.token-display-container {
  margin-bottom: 24px;
}

.token-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.token-input-wrapper {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 4px;
  transition: all 0.2s ease;
}

.token-input-wrapper:focus-within {
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.token-input {
  flex: 1;
  background: transparent;
  border: none;
  padding: 12px 16px;
  font-family: "Monaco", "Consolas", monospace;
  font-size: 0.875rem;
  color: #374151;
  outline: none;
}

.copy-btn {
  margin-right: 8px;
  color: #047B56 !important;
  background: #E6F5F0 !important;
}

.copy-btn:hover {
  background: rgba(5, 150, 105, 0.2) !important;
}

/* Form Sections */
.form-section:not(:last-child) {
  margin-bottom: 20px;
  border-bottom: 1px solid #CCCCCC;
}

.form-section:last-child {
  margin-bottom: 0;
}

.form-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 8px;
}

.modern-input :deep(.v-field) {
  border-radius: 16px;
  border: 1px solid #C9C9C9;
  padding-right: 5px;
}
.modern-input :deep(.v-field__input) {
  padding: 6px 8px 6px 12px;
  min-height: 36px;
}

.modern-input :deep(.v-field__append-inner .mdi-menu-down) {
  color: #C32E0E;
  opacity: 1 !important;
  font-size: 30px;
  margin: 0;
}

.modern-input :deep(.v-field--focused .v-field__outline) {
  color: #3b82f6;
  border-width: 2px;
}

.current-token {
  margin-top: 20px;
}

.token-display {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 4px;
}

.current-token-input {
  flex: 1;
  background: transparent;
  border: none;
  padding: 10px 12px;
  font-family: "Monaco", "Consolas", monospace;
  font-size: 0.8125rem;
  color: #6b7280;
  outline: none;
}

.copy-btn-inline {
  color: #059669 !important;
  margin-right: 4px;
}

/* Radio Group */
.radio-group-modern {
  margin: 0;
}

.radio-option {
  background: #fafbfc;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.radio-option:hover {
  border-color: #d1d5db;
}

.radio-option:has(.v-radio-group input:checked) {
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.radio-option :deep(.radio-input) {
  border-top: none;
}

.custom-radio {
  padding: 16px 20px 16px 12px;
  margin: 0;
  background: white;
}

.form-section  :deep(.radio-input .v-field__input) {
  padding: 8px 12px;
  min-height: 42px !important;
}
.custom-radio :deep(.v-field__field) {
  min-height: 42px !important;
}

.custom-radio :deep(.v-selection-control__wrapper) {
  margin-right: 8px;
}
.custom-radio :deep(.mdi-radiobox-marked) {
  color: #C32E0E;
}

.form-section :deep(.v-input__details) {
  display: none !important;
}

.radio-content {
  display: flex;
  flex-direction: column;
}

.radio-title {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.9375rem;
}

.radio-description {
  font-size: 0.8125rem;
  color: #6b7280;
}

.radio-input {
  padding: 0 20px 16px 20px;
  border-top: 1px solid #e5e7eb;
  background: white;
}

.duration-select-modern :deep(.v-field),
.date-input-modern :deep(.v-field) {
  border-radius: 8px;
  background: white;
}

.duration-select-modern :deep(.v-field__append-inner .mdi-menu-down),
.date-input-modern :deep(.v-field__append-inner .mdi-menu-down) {
  color: #C32E0E;
  opacity: 1 !important;
  font-size: 30px;
}

/* Warning Components */
.warning-container {
  padding: 20px;
}

.warning-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.warning-icon-large {
  background: #fef2f2;
  padding: 20px;
  border-radius: 20px;
  border: 4px solid #fecaca;
}

.warning-message {
  font-size: 0.9375rem;
  color: #374151;
  line-height: 1.6;
  margin: 0 0 16px 0;
}

.highlight {
  background: #fef2f2;
  color: #D52525;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
}

.token-info {
  background: #C9C9C911;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 12px;
  gap: 12px;
  display: flex;
  width: fit-content;
  justify-content: space-between;
  align-items: center;
}

.token-label-small {
  font-size: 0.875rem;
  color: #646464;
}

.token-name-display {
  color: black;
  font-size: 0.875rem;
}

/* Dialog Footer */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 14px 28px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

/* Buttons */
.cancel-btn {
  color: #646464 !important;
  font-weight: 500;
  border-radius: 16px !important;
  border: 1px solid #949AA8 !important;
  padding: 10px 16px;
  height: auto;
}

.cancel-btn:hover {
  transform: none !important;
}

.primary-btn {
  background: linear-gradient(135deg, #326DCF 0%, #1d4ed8 100%) !important;
  color: white !important;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  border-radius: 12px !important;
}

.primary-btn:hover {
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.6);
  transform: translateY(-1px);
}

.danger-btn {
  background: linear-gradient(90deg, #C32E0E 0%, #F67760 100%) !important;
  color: white !important;
  font-weight: 600;
  padding: 10px 16px;
  border-radius: 16px !important;
  text-transform: none !important;
  height: auto;
  box-shadow: none;
}
.danger-btn:hover {
  background: linear-gradient(90deg, #F36243 0%, #FF8E7A 100%) !important;
  box-shadow: none;
}

/* Loading */
.loading-overlay :deep(.v-overlay__scrim) {
  background: rgba(255, 255, 255, 0.8) !important;
  backdrop-filter: blur(4px);
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  background: white;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.loading-text {
  font-weight: 600;
  color: #374151;
  font-size: 1rem;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .docs-layout {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .form-grid-2 {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .external-connector-container {
    padding: 16px;
  }

  .tab-content {
    padding: 16px;
  }
}

.my-editor {
  background: #2B2B2C !important;
  color: #f1f5f9 !important;
  border-radius: 12px;
  font-family: "JetBrains Mono", "Monaco", "Consolas", monospace;
  padding: 10px;
}

.my-editor :deep(.token.comment),
.my-editor :deep(.token.prolog),
.my-editor :deep(.token.doctype),
.my-editor :deep(.token.cdata) {
  color: #868A89;
  padding-right: 4px;
}

.my-editor :deep(.token.punctuation) {
  color: #94a3b8;
}

.my-editor :deep(.token.property),
.my-editor :deep(.token.tag),
.my-editor :deep(.token.boolean),
.my-editor :deep(.token.number),
.my-editor :deep(.token.constant),
.my-editor :deep(.token.symbol),
.my-editor :deep(.token.deleted) {
  color: #f59e0b;
}

.my-editor :deep(.token.selector),
.my-editor :deep(.token.attr-name),
.my-editor :deep(.token.string),
.my-editor :deep(.token.char),
.my-editor :deep(.token.builtin),
.my-editor :deep(.token.inserted) {
  color: #10b981;
}

.my-editor :deep(.token.operator),
.my-editor :deep(.token.entity),
.my-editor :deep(.token.url),
.my-editor :deep(.language-css .token.string),
.my-editor :deep(.style .token.string) {
  color: #06b6d4;
}

.my-editor :deep(.token.atrule),
.my-editor :deep(.token.attr-value),
.my-editor :deep(.token.keyword) {
  color: #8b5cf6;
}

.my-editor :deep(.token.function),
.my-editor :deep(.token.class-name) {
  color: #3b82f6;
}

.my-editor :deep(.token.regex),
.my-editor :deep(.token.important),
.my-editor :deep(.token.variable) {
  color: #ef4444;
}

/* Line numbers styling */
.my-editor :deep(.prism-editor__line-numbers) {
  background: #2B2B2C !important;
  color: #929695 !important;
  border-right: 1px solid #868A89;
}
</style>
